// Sugalarm — alarm "episode" state machine for low / high /
// urgent_low / urgent_high readings.
//
// One alert from the server can drive a multi-step user-facing flow:
//
//   active   – background flashes red, alarm WAV is looping, the
//              overlay shows "Address the Situation".
//   snoozed  – user pressed Address; alarm goes quiet for 5 minutes;
//              background still flashes; overlay shows the countdown.
//   active   – snooze expired and the user hasn't recovered; alarm
//              starts looping again, overlay now shows "Silence".
//   silenced – Silence pressed; alarm goes quiet for the remainder of
//              the episode; background keeps flashing.
//   idle     – episode ends when the recovery rule clears.
//
// IMPORTANT: the episode triggers off the current **reading's**
// effective level (computed locally from the user's thresholds), not
// off new alert frames. The server-side alerts engine is
// edge-triggered — it only writes an Alert row when the level
// transitions, so a sustained-low session sees one alert at the
// crossing and silence afterwards. If we waited for a new frame the
// kiosk would never alarm during that sustained state. We listen to
// alert frames separately to capture an `alertId` for server-side
// snooze / silence when the user presses "Address the Situation".
//
// CYCLE 38: cross-device alarm-state sync. `address()` POSTs
// /api/alerts/{id}/snooze and `silence()` POSTs
// /api/alerts/{id}/silence; both broadcasts arrive back as
// `alert_snoozed` / `alert_silenced` frames on every connected device
// for the same user (cycle 60: across both accounts of a linked
// member). Local optimistic state masks network latency but is
// overridden by the next server-sourced frame.
//
// CYCLE 61: receiving-client adoption fix. `socket.lastAlert` is the
// synchronization key — whenever this device has an active episode
// and the cached alert's level matches the episode's start level,
// the cached alert's `snoozed_until` / `silenced_at` are adopted as
// ground truth. `alertId` still gets bound to `socket.lastAlert.id`
// (so `address()` / `silence()` POST against the right row), but
// it's a *binding* output, not a *gate* on adoption. Recovery stays
// reading-stream-local (every device sees the same readings and
// converges).
//
// CYCLE 63: bulletproof Stop controls + sustained-alarm heartbeat.
//
// Two coupled fixes against a live bug: a sustained low caused the
// alarm to keep sounding while the Stop / Snooze controls vanished
// from the screen on every surface. Three independent break points
// in the cycle-38/61/62 chain could each hide the control:
//   (1) edge-trigger miss (engine writes one Alert row at transition
//       and nothing after; a device that connected mid-low has no
//       phase to drive the banner);
//   (2) local recovery flap (a single in-range reading ended the
//       episode and yanked the control; re-entry needed an
//       edge-trigger the server wouldn't re-emit);
//   (3) `lastAlert` level mismatch (low ↔ urgent_low boundary
//       jitter broke the cycle-61 exact-level adoption).
//
// Frontend cycle-63 floor:
//   - `alarmActive` derived predicate (true iff audio is looping OR
//     the current live reading is in an alarming zone and not stale).
//     Does NOT depend on phase / alertId / lastAlert.level.
//   - `stopNow()` action: stops local audio immediately regardless of
//     whether an alert id is captured; POSTs /acknowledge only when
//     an id is available; NEVER POSTs /silence; never POSTs in
//     synthetic mode.
//   - Family-match adoption: cached `socket.lastAlert` binds when
//     its level is in the same alarm family as `startLevel`
//     (low-family = {low, urgent_low}, high-family = {high,
//     urgent_high}) instead of exact-level match. Boundary jitter
//     no longer strands the control.
//   - Recovery hysteresis: `RECOVERY_DWELL_MS` (default 90 000 ms,
//     ~2 reading intervals). A single in-range reading is not
//     enough to end the episode; recovery requires the reading to
//     clear the alarming zone AND stay cleared for the dwell.
//     Re-entry within the dwell keeps the same episode open. The
//     cycle-62 worse-crossing re-arm path is unchanged — the dwell
//     governs return-to-range *end*, not re-arm.
//
// Server cycle-63 floor: sustained-alarm heartbeat. A new reading
// whose level is unchanged but still alarming refreshes the open
// alert row's `last_heartbeat_at` and emits a throttled
// `alert_heartbeat` WS frame carrying the full refreshed AlertOut.
// Every connected device upserts the row into lastAlert and binds
// `alertId` via the same family-match path above, so a sustained low
// no longer leaves any device without a bindable id.
//
// CYCLE 62: two-button model (Snooze + Stop). Cycle-62 Stop was
// local-only and re-armed client-side on any same-side worse
// crossing. Cycle 63 added the `alarmActive` floor + `AlarmSilenceGuard`.
//
// CYCLE 66 — Stop is now CROSS-DEVICE, server-authored.
//
// The model: every device on a member mirrors **one shared
// alarm-episode state** authored on the server. Stop, Snooze, and
// the single mild→urgent re-arm all happen to the episode (the open
// `Alert` row), and every device reflects via the cycle-12 / 38 / 61
// WS frames + cycle-60 member_audience fan-out.
//
//   - `address()` (Snooze) — unchanged. Server-coordinated 5-min
//     countdown via /snooze; cross-device-synced via alert_snoozed.
//   - `stop()` / `stopNow()` (Stop) — now POSTs **/silence** instead
//     of /acknowledge. Sets `alerts.silenced_at = now` on the open
//     row, broadcasts `alert_silenced` to every device on the
//     cycle-60 member_audience. Each device sees the silenced row
//     via `socket.lastAlert.silenced_at != null` and mutes audio +
//     shows the stopped treatment in lockstep. **Mute is optimistic
//     and never gated on the network round-trip** — the local audio
//     stops on the gesture even if the POST hasn't returned, even if
//     `alertId` hasn't been captured yet.
//   - **Single same-side mild→urgent re-arm**, engine-authored. When
//     a silenced low/high reading worsens to its same-side urgent
//     zone, the alerts engine clears `silenced_at` on the open row
//     and emits an urgent `alert` frame (cycle-12 edge-trigger
//     posture) so every device re-arms audio together. Stop stays
//     available on the re-armed urgent surface — pressing Stop at
//     urgent re-silences the same row, terminal until recovery (no
//     more severe zone exists on the same side).
//   - **No client-side re-arm.** The cycle-62 LEVEL_SEVERITY +
//     stopTimeZone worse-crossing logic is **removed**. Re-arm is
//     observed via the urgent `alert` frame the engine emits.
//
// The cycle-62 limitation "Stop is local-only — pressing Stop on
// the kiosk won't mute the phone in the next room" is **resolved**.
//
// CYCLE 82 — RECOVERY is now server-authored, and the alarm-lifecycle
// fan-out is cross-replica.
//
// The whole episode lifecycle (fired → snoozed → silenced → same-side
// re-arm → recovered) is authored in the alerts engine and persisted
// on the one open `Alert` row. This client is a pure mirror for
// recovery: it no longer runs the cycle-63 local reading-stream dwell.
// When the engine decides the episode has recovered (the member's
// readings sat back inside the in-range band for the dwell), it stamps
// a terminal `recovered_at` on the row and broadcasts an
// `alert_recovered` frame (and the column rides the connect-time
// snapshot, so a late-joiner converges without a press). This device
// ends the episode the instant it sees `recovered_at` on the bound
// row — so two devices with skewed reading streams clear together
// instead of seconds apart. The reading-stream trigger that OPENS the
// episode locally is kept (the engine is edge-triggered; a device
// mid-low needs the open), but recovery is no longer local.
//
// The cross-replica seam (Postgres LISTEN/NOTIFY in `ws_manager` +
// `alarm_pubsub`) means the cycle-66 "single-replica fan-out caveat"
// no longer applies to alarm frames — a Stop/Snooze/recover on any
// replica reaches every device. (The deployment still runs a single
// replica by deliberate choice; the seam just makes 2+ safe.)
//
// `synthetic: true` short-circuits the alerts API calls from
// `address()` / `silence()` so TestAlarmDriver can drive the same
// overlay against fake alert ids.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  TREATING_WINDOW_SECONDS,
  pickLiveUrgentRow,
  refusalMeansStaleRow,
  treatingErrorCode,
} from "../lib/treating";

import * as alertsApi from "../api/alerts";
import {
  CHECKIN_REQUEST_TIMEOUT_MS,
  checkinKey,
  checkinPendingAt,
} from "../lib/treatingCheckin";
import { effectiveEpisodeLevel } from "../lib/episodeLevel";
import { recordClientEvent } from "../lib/clientEvents";
import { lowRungLatched } from "../lib/ascentLatch";
import { alarmDisplaySgv } from "../lib/alarmDisplaySgv";
import { episodeRowClosedByServer } from "../lib/episodeTruth";
import { ackBackoffMs, ackIsRetryable } from "../lib/ackRetry";
import type { AckAction, AckFailure } from "../lib/ackRetry";
import type {
  CustodyCheckinAnswer,
  CustodyCheckinOutcome,
  CustodyCheckinQuestion,
} from "../lib/types";
import type { CheckinEvent, CustodyHandBack } from "./useGlucoseSocket";
import { isStale } from "../lib/glucose";
// Cycle 398: the in-range stand-down predicate + the audio derivation that
// hangs off it, extracted so the hook and its golden vectors read the SAME
// rule. See `lib/alarmStandDown.ts` for the "standDown ⇒ ¬audioOn" argument.
import {
  audioOnFor,
  inRangeStandDown,
  recoveringFromUrgentLow as recoveringFromUrgentLowFor,
} from "../lib/alarmStandDown";
// Cycle 410: the lead window's client half — the receipt-capped deadline
// and the descent rule. AUDIO ONLY; the slot is `preferSoundingRow`'s.
import {
  effectiveLeadDeadline,
  effectivePendingDeadline,
  noDataQuietHolds,
  selectAudibleRow,
  type AudibleCandidate,
} from "../lib/audibleRow";

import type { GlucoseSocketState } from "./useGlucoseSocket";
import type { AlertLevel, TreatingBy } from "../lib/types";

const SNOOZE_MS = 5 * 60 * 1000;

// Cycle 82: the cycle-63 client-side `RECOVERY_DWELL_MS` hysteresis is
// GONE. Recovery is server-authored now — the engine tracks the dwell
// (relocated to `RECOVERY_DWELL_SECONDS` in alerts_service) from
// reading timestamps and stamps `recovered_at` on the open row; this
// client just mirrors that column. No local dwell timer remains.

// Cycle 36.1: `high` joins the alarming set so the new
// HighAlarmBanner (symmetric companion to LowAlarmBanner) fires on
// real high-side readings, not just on previews.
const ALARMING_LEVELS: ReadonlySet<AlertLevel> = new Set([
  "low",
  "high",
  "urgent_low",
  "urgent_high",
]);

export type EpisodePhase = "idle" | "active" | "snoozed";

export interface AlarmEpisodeState {
  phase: EpisodePhase;
  /** Effective level from the most recent reading (or null if recovered). */
  level: AlertLevel | null;
  /** Most recent reading sgv — used by the overlay's hero number. */
  sgv: number | null;
  /** Most recent matching alert id, threaded through for server-side
   *  snooze / silence. Null on sustained alarms with no captured row. */
  alertId: string | null;
  /** Derived: phase === "active" && !silenced && !stopped. */
  audioOn: boolean;
  /** True after the user has pressed Snooze (formerly Address) at
   *  least once this episode. Cycle 61: derived from
   *  `socket.lastAlert.snoozed_until` whenever the cached alert's
   *  level matches the episode's start level. */
  hasSnoozed: boolean;
  /** True after the user has pressed Silence this episode.
   *  Cycle 61: derived from `socket.lastAlert.silenced_at` under the
   *  same level-match condition (see `hasSnoozed`).
   *  Cycle 62: the user-facing Silence button is gone; this stays in
   *  the surface for the server-coordinated `alert_silenced` frame
   *  (still emitted by the cycle-38 wire surface). No UI driver
   *  this cycle. */
  silenced: boolean;
  /** Cycle 62 + cycle 66: shared cross-device "Stop pressed" flag.
   *  Aliased onto `silenced` since cycle 66 unified the two — Stop
   *  POSTs /silence and the server-authored `silenced_at` on the
   *  open alert row is the one true source. Kept as a distinct
   *  field on the surface so existing alarm-surface components
   *  (UrgentOverlay / banners / NowScreen / AlarmSilenceGuard) that
   *  read `episode.stopped` continue to work; they get the
   *  cross-device shared state for free. */
  stopped: boolean;
  /** Milliseconds remaining in the snooze, while phase === "snoozed". */
  snoozeRemainingMs: number | null;
  /** Cycle 391: a treating window is OPEN right now — somebody at a
   *  screen declared they are treating this urgent low.
   *
   *  Derived from `socket.lastAlert.custody_until` on the same footing
   *  the cycle-38/61 machinery derives `hasSnoozed`: the server row is
   *  ground truth, local state is optimistic-then-reconciled, and
   *  adoption is NOT gated on a locally-latched `alertId` (that was the
   *  cycle-61 bug — a device that never saw the original `alert` frame
   *  silently dropped the state).
   *
   *  It is NOT Stop. The visual alarm stays fully rendered, the kiosk
   *  keeps a soft periodic tick, the window self-expires, and any
   *  worsening or stale reading tears it down server-side. */
  treating: boolean;
  /** The server wall clock the window ends at (epoch ms), or null.
   *  Every device counts down from the SAME server value, so the kiosk
   *  and the phones tick in lockstep. */
  treatingUntil: number | null;
  /** Milliseconds left in the window, or null. Recomputed from the
   *  server wall clock on every tick — never a locally-started timer. */
  treatingRemainingMs: number | null;
  /** Windows opened during THIS episode. Drives the engagement cap in
   *  the sheet: at 2 or more the client offers only 5 minutes (and the
   *  server enforces it regardless of what a client asks for). */
  treatingCount: number;
  /** Cycle 393: WHICH KIND of custody is open — `"declared"` (somebody
   *  said they are treating this) or `"quiet"` (somebody asked for
   *  silence, i.e. a snooze). `null` when none is open.
   *
   *  ⚠ `treating` above is true ONLY for `"declared"`. Snooze opens
   *  custody too — it holds the ladder and spends engagement budget — but
   *  it declares nothing, so it keeps the existing Snoozing affordance
   *  and never renders the treating surface. */
  custodyKind: "declared" | "quiet" | null;
  /** Cycle 409: a QUIET window (a snooze on an urgent low) is open right
   *  now. It mounts the same timer screen a declaration does — every
   *  device shows the ring counting down off the server's `custody_until`
   *  — with honest copy ("Snoozed", not "treating") and an "I'm treating
   *  this" control to convert it. `treating` stays declared-only, so the
   *  audio gate, the check-in and the inference are untouched. */
  quietWindow: boolean;
  /** Cycle 410: a LEAD WINDOW is open on the bound urgent-low row — the
   *  phone calls are running and this device holds the urgent audio
   *  UPGRADE (and nothing else) until `audioLeadUntil`, on its own clock.
   *  Display + audio only: it gates no POST, never moves the slot, and
   *  is false the instant custody opens, the row closes, the deadline
   *  passes on this device, or a frame carries `audio_lead_until: null`. */
  audioLead: boolean;
  /** Epoch ms this device sounds at — `min(server deadline, first receipt
   *  + 90 s)` — or null. The dead-man switch. */
  audioLeadUntil: number | null;
  audioLeadRemainingMs: number | null;
  /** Cycle 410.1: the PENDING GRACE is holding this device's urgent-low
   *  audio — the crossing frame said a lead window is eligible and the
   *  SID has not come back yet. ≤3 s from the frame's receipt, capped
   *  locally; resolved by the next frame for the row or by the clock.
   *  Every ambiguous outcome is LOUD. False whenever a window is open. */
  audioLeadPending: boolean;
  audioLeadPendingUntil: number | null;
  /** Cycle 410: WHICH row's audio plays. Equal to `level` whenever no
   *  lead window is in play (byte-identical to today); under a window it
   *  is the open, un-quieted `low` sibling's level on a descent, or null
   *  on a cold crossing. `useAlarmSounds` drives the loop off THIS. */
  audibleLevel: AlertLevel | null;
  /** Cycle 424: the low-rung latch, derived on this device from the bound
   *  row exactly as the server derives it (`lib/ascentLatch.ts`): the
   *  bound row is an open, unrecovered, un-silenced `urgent_low` whose
   *  ascent is not broken, and the live reading is in the plain-low band —
   *  the member is climbing out. While true the surfaces keep the URGENT
   *  row's cue (never a low cue) and say why the low rung is quiet. Never
   *  a gate on any control; never quieter than the server already is. */
  ascentLatched: boolean;
  /** Cycle 410: is anything actually sounding for this member right now
   *  (`audioOn && audibleLevel != null`). The lead screen shows Snooze
   *  if and only if this is true. */
  audible: boolean;
  /** Cycle 410: "Sound the alarm now" — ends the lead window for every
   *  device (one direction: louder). This device sounds on the tap; the
   *  server's teardown frame does the rest. Resolves to an error code on
   *  a refusal, null on success. */
  soundNow: () => Promise<string | null>;
  /** Cycle 391 "I'm treating this" — POSTs the new endpoint with one of
   *  300 / 600 / 900. Resolves to an error code on refusal (notably
   *  `no_fresh_reading`) so the surface can say WHY rather than showing
   *  a generic failure over a live alarm. */
  treat: () => Promise<string | null>;
  /** Cycle 456: the `custody_opened_at` this device's last SUCCESSFUL treat
   *  POST returned — the window its own tap opened (null when unknown). The
   *  "Who's treating?" latch asks about that window and no other. */
  lastTreatWindow: () => string | null;
  /** Cycle 392: the resolved display name of whoever declared themselves,
   *  or null while the window is anonymous. Server-resolved — the client
   *  never sees a contact id. */
  treatingContactName: string | null;
  /** Cycle 456: who took the open window (server-resolved), or null. */
  treatingBy: TreatingBy | null;
  /** Cycle 456: the open window's identity (`custody_opened_at`), or null. */
  custodyOpenedAt: string | null;
  /** Cycle 456: the picker's server-owned timeout, in seconds. */
  attributionPickerSeconds: number;
  /** Cycle 409: how many heroes "I need help now" would reach from this
   *  window (server-computed: verified heroes minus a named acceptor).
   *  `null` = unknown → the screen shows the button. */
  backupReach: number | null;
  /** Cycle 392 — the SECOND tap. Names an already-running window; it can
   *  never open, extend, shorten or close one, and skipping it costs the
   *  caregiver nothing. `null` is the explicit "Someone else" answer.
   *
   *  Optimistic and deliberately quiet on failure: the confirmation comes
   *  back over `alert_treating`, and a failed post must NOT reopen the
   *  sheet or raise an error dialog over a live urgent low. */
  setTreatingIdentity: (contactId: string | null) => Promise<void>;
  /** Cycle 404: the contact ids that pressed 3 ("I can't help right now")
   *  on THIS episode's escalation calls, straight off the bound alert row.
   *  Null when no server row is bound yet, or on a payload predating the
   *  cycle; consumers treat null and `[]` identically.
   *
   *  ⚠ READ-ONLY, AND DISPLAY-ONLY. Nothing on this surface suppresses a
   *  call — the server owns the call search and the suppression. This
   *  exists so a hero looking at their own phone can see they are out and
   *  put themselves back in (`optOutUndoVisible`). */
  episodeOptOuts: string[] | null;
  /** Cycle 407: the check-in. `checkinQuestion` / `checkinExpiresAt` are
   *  the server row's pending question (the persisted state a restarted
   *  kiosk resumes from); `checkinPending` is true while it is on screen
   *  and still answerable — and NOT already answered from this device
   *  (local optimism that only ever HIDES a question, never shows one);
   *  `checkinEvent` is the live `custody_checkin` frame for this alert,
   *  the edge the screen speaks on; `treatingExtensionCount` is how many
   *  bounded windows answers have bought this episode. */
  checkinQuestion: CustodyCheckinQuestion | null;
  checkinExpiresAt: string | null;
  checkinPending: boolean;
  checkinEvent: CheckinEvent | null;
  treatingExtensionCount: number;
  /** Cycle 427: the live prompt on the bound row — `checkinQuestion` /
   *  `checkinExpiresAt` under their new names (the kind is one of
   *  `checkin` / `worsening` / `still_treating`; the deadline is the
   *  server's, counted down on this device's own clock). Pending on a
   *  window of EITHER kind: the dark-expiry check-in and the still-
   *  treating question are posed on a snooze too. */
  promptKind: CustodyCheckinQuestion | null;
  promptDeadline: string | null;
  /** Cycle 427: help asked for beneath the OPEN window — `calling` while
   *  the ladder walks the other heroes, `accepted` once one pressed 1 —
   *  and the hero's resolved name. Display only. */
  helpState: "calling" | "accepted" | null;
  helpContactName: string | null;
  /** Cycle 427: the dark-feed tone rule holds on THIS device: the server
   *  flagged the bound row (`no_data_quiet`), this device can see the
   *  reading is stale, and no window is open. Audio selection only —
   *  `audioOn`, the controls and every gate are untouched; the takeover
   *  stays with the number greyed. */
  noDataQuiet: boolean;
  /** Cycle 407 → 427: answer the prompt on screen (with its KIND, so a
   *  stale answer to a superseded prompt is refused), or press "I need
   *  help now" (`help`) / "Resume alerts" (`resume`). Resolves to the
   *  server's outcome, or null when nothing was sent (no bound alert,
   *  synthetic, or a failed POST — in which case the question is
   *  un-hidden again). The glucose side is decided SERVER-side; nothing
   *  local extends anything. */
  checkin: (
    answer: CustodyCheckinAnswer,
    kind?: CustodyCheckinQuestion | null,
  ) => Promise<CustodyCheckinOutcome | "timeout" | null>;
  /** Cycle 407: why the last treating window on this member ended —
   *  the hand-back reason line's source. Null until a teardown frame. */
  custodyHandBack: CustodyHandBack | null;
  /** "Snooze" — POSTs /api/alerts/{id}/snooze; cross-device-synced
   *  via the cycle-38 `alert_snoozed` broadcast. */
  address: (opts?: { retry?: boolean }) => Promise<void>;
  /** Cycle 416 — non-null when an ack (Snooze / Stop) did NOT land.
   *
   *  ⚠ EVERY ALARM SURFACE THAT OFFERS AN ACK MUST RENDER THIS. Before this
   *  cycle a failed ack was swallowed and the banner rendered an ordinary
   *  snoozed / stopped state, so the caregiver believed the alarm was
   *  handled when it was not. Non-null means the alarm may still be live.
   *  Pair it with `retryAck` — never with a bare dismiss.
   *
   *  Cleared only by a later successful ack, the end of the episode, or the
   *  adoption of a new one. An incoming frame does NOT clear it. */
  ackFailure: AckFailure | null;
  /** Cycle 416 — re-attempt the ack that failed. No-op when null. */
  retryAck: () => void;
  /** "Silence" — internal cycle-38 wire driver. Cycle 66: `stop()`
   *  and `stopNow()` route through this same POST `/silence` path
   *  so Stop converges with the cycle-38 cross-device wire. Kept on
   *  the surface for any synthetic / test caller that wants the raw
   *  silence handle without the bulletproof open-episode logic. */
  silence: () => void;
  /** Cycle 62 + cycle 66 "Stop" — cross-device mute via POST
   *  /api/alerts/{id}/silence. Optimistic local audio mute fires
   *  immediately on the gesture; the POST propagates the silence to
   *  every other device on the cycle-60 member_audience via
   *  `alert_silenced`. The cycle-12 one-row-per-episode contract
   *  holds — the silence sets `silenced_at` on the existing open
   *  row. A same-side mild→urgent escalation later clears
   *  `silenced_at` server-side and re-arms every device. */
  stop: () => void;
  /** Cycle 63 + cycle 66 "Stop (bulletproof)" — guaranteed mute.
   *  Same cross-device path as `stop()` but never gated on
   *  `isActive` or `alertId`: opens the local episode if it isn't
   *  already so the post-Stop treatment renders, optimistically
   *  mutes audio, and POSTs `/silence` when an id is available.
   *  When no id is captured yet (mid-low / pre-heartbeat), the
   *  POST is queued and fires as soon as the next heartbeat /
   *  snapshot lands an id — never a local-only no-op. No-ops the
   *  POST in synthetic / preview mode. */
  stopNow: () => void;
  /** Cycle 63 floor: true whenever alarm audio is looping OR the
   *  current live reading sits in an alarming zone and is not
   *  stale. Does NOT depend on `phase` / `alertId` /
   *  `socket.lastAlert.level`.
   *
   *  Cycle 81: `alarmActive` is RETAINED for cosmetic / zone use
   *  (it still tracks the live reading's zone) but it is **no longer
   *  the control-visibility gate** anywhere — control surfaces gate
   *  on `isLive` instead (see below). The audio bridge in
   *  `useAlarmSounds` reads `audioOn` / `level`, not this. */
  alarmActive: boolean;
  /** Cycle 81: the single, unambiguous "this episode is live"
   *  predicate — `phase === "active" || phase === "snoozed"`. Every
   *  alarm CONTROL surface (the banners, NowScreen's banner mounts +
   *  inline urgent view, and the `AlarmSilenceGuard` floor) gates on
   *  this, decoupled from `alarmActive` (the live reading's zone) and
   *  from the live reading level. Because `audioOn ⇒ phase ===
   *  "active" ⇒ isLive`, gating controls on `isLive` makes the
   *  invariant hard: if the alarm loop could be sounding, a control
   *  is on screen — with no remaining recovery-dwell gap. Local and
   *  forward-compatible: when cycle 82 drives `phase` from server
   *  state this predicate keeps working unchanged. */
  isLive: boolean;
  /** Cycle 81: the persisted / effective episode level used to decide
   *  WHICH control surface a live episode renders — independent of
   *  the live reading's instantaneous zone. Equals `level`
   *  (`currentLevel`) when the reading is still in an alarming zone,
   *  but falls back to the episode's `startLevel` during the
   *  recovery-dwell window, when the reading has risen/fallen back
   *  into range (`currentLevel` is null) while the episode is still
   *  live. A more-severe live reading still wins (so the
   *  engine-authored same-side mild→urgent re-arm flips the surface
   *  to the urgent takeover). When idle, `startLevel` is null so this
   *  is just `currentLevel`. Banners / NowScreen mounts gate on this;
   *  `level` stays the live-reading zone so the audio bridge and the
   *  cosmetic zone pill are byte-identical. */
  episodeLevel: AlertLevel | null;
  /** Cycle 311.1: the level the episode OPENED at (null when idle). The
   *  OTA gate ORs its family with `episodeLevel`'s so a LOW-opened
   *  episode holds the install gate structurally even if the current
   *  reading family-jumps — "lows can never be bypassed". */
  startLevel: AlertLevel | null;
  /** Cycle 304, WIDENED IN CYCLE 398: derived DISPLAY predicate — true
   *  while a LIVE episode in ANY phase (active, snoozed, silenced/stopped,
   *  or inside a cycle-391 treating window) has a FRESH IN-RANGE latest
   *  reading for the alarming member that is STRICTLY NEWER than the
   *  reading which armed or last re-armed the episode.
   *
   *  Every alarm display surface hides when true, so the banner — and the
   *  urgent full-screen takeover — clears on the FIRST in-range reading
   *  instead of waiting out the server's 90 s recovery dwell (a SECOND
   *  in-range reading on a 5-minute CGM cadence, i.e. minutes of stale red
   *  on the tablet and the phone after the household is already fine).
   *
   *  It does NOT end the episode. `phase`, the snooze countdown,
   *  `startLevel`, the bound row and the server's authority over
   *  `recovered_at` are untouched; the server still owns recovery.
   *
   *  ⚠ THE CYCLE-81 CONTROL FLOOR IS NOT WEAKENED — IT IS RE-DERIVED FROM
   *  THE OTHER SIDE. Cycle 304 guaranteed `audioOn ⇒ ¬suppressed` by
   *  excluding active episodes. Cycle 398 guarantees `suppressed ⇒
   *  ¬audioOn` by DERIVING `audioOn` from this predicate. Both are
   *  `¬(audioOn ∧ suppressed)`; the difference is that audio now stops in
   *  the SAME RENDER the banner hides, so there is never a frame in which
   *  the loop sounds with no control on screen.
   *
   *  Re-arm is free: the very next alarm-side reading flips this false and
   *  the banner AND the audio return in the same render, with no new alert
   *  row and no server round trip. Stale, missing, wrong-member, synthetic-
   *  preview, older-than-arm, or alarm-side readings → false.
   *
   *  ⚠ CYCLE 428: ALWAYS `false` FOR A LIVE EPISODE. The early stand-down is
   *  removed (`inRangeStandDown` returns false); the display clears only on
   *  the server's recovery frame, which ends the episode. The field and the
   *  gates that read it are kept so the audio derivation and its invariant
   *  are unchanged. */
  bannerSuppressedInRange: boolean;
  /** Cycle 398 edge key; since cycle 428 always null (never stood down). */
  inRangeStandDownAt: number | null;
  /** Cycle 428 — v3 "Coming back up": true while the urgent-low row is still
   *  open and the latest fresh reading is ABOVE the urgent-low line — the
   *  whole climb, band included (a high-side reading counts). Drives ONLY the
   *  header sentence ("Recovering from an urgent low, low alerts are held
   *  until [member] is back in range") on the takeover and the treating
   *  screen — never the controls, the ring, or the sound. */
  recoveringFromUrgentLow: boolean;
  /** TestAlarmDriver synthetic dismiss — clears state without server. */
  endEpisode: () => void;
}


/** Cycle 63 family-match adoption helper: low + urgent_low share a
 *  family on the low side, high + urgent_high on the high side.
 *  Boundary jitter between the two members of a family must not
 *  strand the Stop control or break alertId binding. */
function sameFamily(a: AlertLevel | null, b: AlertLevel | null): boolean {
  if (a == null || b == null) return false;
  const low = (l: AlertLevel) => l === "low" || l === "urgent_low";
  const high = (l: AlertLevel) => l === "high" || l === "urgent_high";
  return (low(a) && low(b)) || (high(a) && high(b));
}


// Cycle 66: the cycle-62 LEVEL_SEVERITY + stopTimeZone client-side
// worse-crossing re-arm machinery is retired. Same-side mild→urgent
// re-arm is now server-authored — the alerts engine clears
// `silenced_at` on the open row and emits the urgent `alert` frame,
// and every device just reacts to that frame the same way it reacts
// to any fresh urgent alert.

export interface AlarmThresholds {
  urgent_low: number;
  target_low: number;
  target_high: number;
  urgent_high: number;
}

export interface UseAlarmEpisodeOpts {
  /** settings.alerts_enabled — false silences and ends any episode. */
  enabled: boolean;
  socket: GlucoseSocketState;
  thresholds: AlarmThresholds;
  /** Skip the alerts API calls — used by TestAlarmDriver. */
  synthetic?: boolean;
  /** Cycle 310: the server-truth reconciliation just cleared an episode
   *  that was LATCHED past server-authored recovery (the OTA-wedge
   *  signature). Telemetry only — the caller relays it to the kiosk
   *  heartbeat so fleet recurrence is visible in the admin roster. */
  onWedgeCleared?: (info: {
    episodeAgeSeconds: number;
    activeMemberNull: boolean;
  }) => void;
}

// ─── Cycle 310/342: server-truth reconciliation cadence ─────────────────
// A latched episode is re-checked against the server on this cadence. The
// FIRST check waits out the server's recovery dwell (RECOVERY_DWELL_SECONDS
// = 90 s in alerts_service — the mirror of that constant) so a genuinely
// recovering episode always gets the normal `alert_recovered` path first;
// after that, once a minute. Runs whenever an episode is latched — Cycle
// 342 removed the earlier "audio is NOT sounding" exclusion (see the
// effect below) once a phantom sounding episode with no server-side row
// proved that exclusion's premise false. It can still never touch a
// GENUINE live alarm: clearing requires the server to affirmatively
// report zero active alerts across every accessible member, which a real
// open row prevents for as long as it's actually open.
const SERVER_TRUTH_RECONCILE_FIRST_MS = 90_000;
const SERVER_TRUTH_RECONCILE_MS = 60_000;


function levelForReading(
  sgv: number,
  t: AlarmThresholds,
): AlertLevel | null {
  if (sgv < t.urgent_low) return "urgent_low";
  if (sgv < t.target_low) return "low";
  if (sgv > t.urgent_high) return "urgent_high";
  if (sgv > t.target_high) return "high";
  return null;
}

// Cycle 82: the client-side `recovered()` reading-trend rule is GONE.
// Recovery is decided by the engine and arrives as `recovered_at` on
// the open Alert row (the `alert_recovered` frame + connect snapshot).

export function useAlarmEpisode(
  opts: UseAlarmEpisodeOpts,
): AlarmEpisodeState {
  const { enabled, socket, thresholds, synthetic = false, onWedgeCleared } =
    opts;

  // Local state is the source of truth for "is there an episode
  // active right now" — the trigger / recovery logic still runs
  // client-side off the reading stream. Cycle 38 overlays server-
  // sourced snooze + silence on top of this.
  const [isActive, setIsActive] = useState(false);
  const [startLevel, setStartLevel] = useState<AlertLevel | null>(null);
  const [alertId, setAlertId] = useState<string | null>(null);
  // Fallback local state for the no-server-id path. Ignored when the
  // alert id is latched (we read from socket.lastAlert in that case).
  const [localSnoozeUntil, setLocalSnoozeUntil] = useState<number | null>(
    null,
  );
  const [localSilenced, setLocalSilenced] = useState(false);
  // Optimistic local state used between user gesture and the next
  // alert_snoozed / alert_silenced WS frame. Reset whenever the
  // latched alert id changes or the server row catches up.
  // Cycle 412: a server refusal of `episode_closed` (a snooze or Stop that
  // landed on a row the server already recovered — a STALE SLOT) asks the
  // cycle-310 server-truth reconciliation to run NOW instead of at its next
  // tick, so the dead row is dropped and this device re-seeds.
  const [reconcileNonce, setReconcileNonce] = useState(0);
  const consumedReconcileNonceRef = useRef(0);
  const [optimisticSnoozeUntil, setOptimisticSnoozeUntil] = useState<
    number | null
  >(null);
  const [optimisticSilenced, setOptimisticSilenced] = useState(false);
  // ─── Cycle 416: a failed ack is VISIBLE ──────────────────────────────
  // ⚠ THE INVARIANT THIS FIELD EXISTS TO ENFORCE: an ack that did not land
  // must never leave the UI looking handled. Before this cycle every ack
  // POST sat in a swallowed `catch {}` and a failure rendered as an ordinary
  // snoozed or stopped banner — the caregiver had no reason to doubt it.
  // Non-null here means the opposite: the alarm may still be live.
  //
  // Cleared by exactly three things: a later ack that succeeds, the end of
  // the episode (`reset`), and the adoption of a new episode. NOT by an
  // incoming frame — a frame re-describing the episode is not evidence that
  // the user's ack landed.
  const [ackFailure, setAckFailure] = useState<AckFailure | null>(null);
  const ackAttemptRef = useRef(0);
  // Ref mirror so `retryAck` stays a stable callback the surfaces pass
  // straight into onClick while still reading the CURRENT failure.
  const ackFailureRef = useRef<AckFailure | null>(null);
  ackFailureRef.current = ackFailure;

  // ─── Cycle 416: run one ack, retry what is worth retrying, surface the rest ───
  //
  // `post` performs ONE attempt. `runAck` owns the retry ladder and the
  // failure state; the callers own the optimistic flip and its withdrawal,
  // because what a refusal means for the AUDIO differs per control (cycle
  // 409/410) and this helper must not second-guess it.
  const runAck = useCallback(
    async (
      action: AckAction,
      post: () => Promise<Awaited<ReturnType<typeof alertsApi.silence>>>,
    ) => {
      const seq = ++ackAttemptRef.current;
      let attempts = 0;
      for (;;) {
        attempts += 1;
        let r: Awaited<ReturnType<typeof alertsApi.silence>>;
        try {
          r = await post();
        } catch {
          // ⚠ A THROW IS A NETWORK FAULT, NOT A REFUSAL. This is the one
          // catch on the ack path, and it converts rather than swallows.
          r = {
            ok: false,
            error: { kind: "network", status: 0, message: "request failed" },
          } as Awaited<ReturnType<typeof alertsApi.silence>>;
        }
        // A newer ack superseded this one — drop this ladder rather than let
        // a stale retry stamp a failure over a fresh success.
        if (ackAttemptRef.current !== seq) return r;
        if (r.ok) {
          setAckFailure(null);
          return r;
        }
        const kind = r.error?.kind ?? "network";
        const wait = ackIsRetryable(kind) ? ackBackoffMs(attempts) : null;
        if (wait == null) {
          setAckFailure({
            action,
            reason:
              kind === "network" || kind === "server" ? "network" : "rejected",
            status: r.error?.status ?? null,
            attempts,
            code: treatingErrorCode(r.error) ?? null,
          });
          return r;
        }
        await new Promise((res) => setTimeout(res, wait));
        if (ackAttemptRef.current !== seq) return r;
      }
    },
    [],
  );
  // Cycle 407: the check-in this device just answered (a `checkinKey`),
  // so the overlay hides on the tap instead of on the round-trip. Only
  // ever HIDES; cleared when the POST fails.
  const [answeredCheckinKey, setAnsweredCheckinKey] = useState<string | null>(
    null,
  );
  // Cycle 391: optimistic treating window, masking the round-trip between
  // the tap and the `alert_treating` frame. Server truth always wins the
  // moment it lands (see the derivation below) — this only covers the
  // gap, exactly like `optimisticSnoozeUntil`.
  const [optimisticTreatingUntil, setOptimisticTreatingUntil] = useState<
    number | null
  >(null);
  // Cycle 410: the row whose lead window THIS device ended by tapping
  // "Sound the alarm now". Loud direction only — it can make this device
  // sound before the teardown frame lands, never hold it quiet.
  const [leadOverriddenAlertId, setLeadOverriddenAlertId] = useState<
    string | null
  >(null);
  // Cycle 66: pending-silence queue. If the user presses Stop before
  // an `alertId` is captured (mid-low / pre-heartbeat), the optimistic
  // local mute fires immediately and the POST is deferred. The
  // alertId-watch effect below fires the queued POST as soon as the
  // next heartbeat / snapshot lands an id — never a dead, local-only
  // no-op. Stays a ref (not state) so we don't trigger a render just
  // to flip it.
  const pendingSilenceRef = useRef(false);
  // Cycle 182 (deliverable 4): the alert id of an episode we cleared
  // because the user changed thresholds (the live reading is no longer
  // in an alarming zone). Recorded so the cycle-61 late-joiner effect
  // below can't immediately re-open the same row — which is NOT
  // server-recovered, only locally cleared — while it sits in a
  // snooze/silence window and the reading stays non-alarming. Released
  // the instant the reading re-enters an alarming zone (a real
  // hypo/hyper must always be able to re-open + re-fire). A ref — no
  // render needed to flip it.
  const thresholdClearedAlertIdRef = useRef<string | null>(null);
  // Forces a re-render once per second so the snooze countdown updates
  // smoothly when read from server `snoozed_until`.
  const [, setNowTick] = useState(0);

  // Cycle 310: when the current episode opened (wall clock) — the wedge
  // telemetry's episode-age source. A ref (no render needed).
  const episodeStartedAtRef = useRef<number | null>(null);

  // ─── Cycle 398: the arming reading's own timestamp ──────────────────
  // Epoch ms of the READING (not the wall clock, not `received_at`) that
  // armed — or last re-armed — this episode. STATE, not a ref, because the
  // stand-down predicate is computed during render and the arm must land in
  // the same commit as `setIsActive(true)`.
  //
  // ⚠ THIS IS THE CONDITION THAT KEEPS A LIVE ALARM ALIVE. `lastReading`
  // can move BACKWARD: a connect snapshot seeds it, a reconnect replays it,
  // and a provider backfill can insert an older row and broadcast it. Any
  // of those could hand us an OLD in-range value in the middle of a genuine
  // low. Requiring the stand-down reading to be STRICTLY NEWER than this
  // means only forward progress can stand an alarm down.
  //
  // "Re-armed" is the second half: every fresh alarm-side reading during a
  // live episode advances it, so after a re-dip the next in-range reading
  // must be newer than the RE-DIP, not merely newer than the original
  // crossing.
  const [armedReadingTsMs, setArmedReadingTsMs] = useState<number | null>(
    null,
  );

  const reset = useCallback(() => {
    setIsActive(false);
    setStartLevel(null);
    setAlertId(null);
    setArmedReadingTsMs(null);
    setLocalSnoozeUntil(null);
    setLocalSilenced(false);
    setOptimisticSnoozeUntil(null);
    setOptimisticSilenced(false);
    // Cycle 391: treating state dies with the episode so it can never
    // leak into the next one. The COUNT is not local state at all — it
    // lives on the alert row, and one row IS one episode (cycle 12), so
    // a fresh low starts everyone back at the full 20-minute option by
    // construction rather than by a reset we could forget to write.
    setOptimisticTreatingUntil(null);
    // Cycle 416: a failure notice dies with the episode it belonged to.
    setAckFailure(null);
    pendingSilenceRef.current = false;
    episodeStartedAtRef.current = null;
  }, []);

  // ─── Trigger / recovery from the current reading ─────────────────
  const reading = socket.lastReading;
  const sgv = reading?.sgv ?? null;
  const currentLevel =
    sgv != null ? levelForReading(sgv, thresholds) : null;
  // Cycle 398: the reading's OWN timestamp in epoch ms (the CGM sample
  // time, not `received_at` — a poll-cycle clock shared by every member's
  // rows is not evidence of sample order for one member).
  const readingTsMs = (() => {
    const raw = reading?.timestamp;
    if (raw == null) return null;
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : null;
  })();

  useEffect(() => {
    if (!enabled) {
      if (isActive) reset();
      return;
    }
    if (sgv == null) return;

    if (!isActive) {
      // Start an episode when the current reading is alarming. This
      // local reading-stream OPEN is kept (the engine is edge-
      // triggered; a device mid-low needs the open). RECOVERY, by
      // contrast, is server-authored (cycle 82) — see the
      // `recovered_at` effect below.
      if (currentLevel && ALARMING_LEVELS.has(currentLevel)) {
        // Cycle 91: don't locally re-open from the reading stream while
        // the only bound alert row is server-RECOVERED for the same
        // family. `recovered_at` is terminal; the recovery effect below
        // would reset the episode again on the very next render, so a
        // still-alarming reading + a recovered row oscillate every tick —
        // the banner flaps and Snooze becomes un-clickable. A genuine
        // continued/re-dipped low re-fires a FRESH non-recovered alert row
        // server-side (the recovered row no longer suppresses the
        // edge-trigger — see alerts_service._most_recent_level), and that
        // fresh row clears this guard so the episode opens and stays open.
        const bound = socket.lastAlert;
        if (
          bound != null &&
          bound.recovered_at != null &&
          sameFamily(bound.level, currentLevel)
        ) {
          return;
        }
        // Cycle 310 (creation guard): never LATCH an episode without a
        // resolvable member context. The active-member-gated reading
        // stream shouldn't deliver readings in that state — but a stale
        // `lastReading` from before the pointer went null could still
        // satisfy `currentLevel`, and an episode opened here would have
        // no clearable server row context (the two-unit fleet wedge).
        // This suppresses only the unclearable LOCAL latch — the server
        // alert rails (push/SMS/voice) are untouched, and the reading
        // stream resuming (member context restored) opens normally.
        if (socket.activeMemberId == null) {
          // eslint-disable-next-line no-console
          console.warn(
            "[alarm] episode open skipped: no active member context",
          );
          return;
        }
        setIsActive(true);
        setStartLevel(currentLevel);
        // Cycle 398: the reading that ARMS the episode is the floor every
        // later stand-down must beat. Set in the same commit as
        // `setIsActive(true)` so the predicate can never see a live
        // episode with no arm timestamp.
        setArmedReadingTsMs(readingTsMs);
        episodeStartedAtRef.current = Date.now();
        setLocalSnoozeUntil(null);
        setLocalSilenced(false);
        setOptimisticSnoozeUntil(null);
        setOptimisticSilenced(false);
        setAckFailure(null); // cycle 416: per-episode, like the flags above
        // Cycle 66: pending-silence flag never carries across
        // episodes — `reset()` clears it on episode end.
        pendingSilenceRef.current = false;
      }
      return;
    }

    // ─── Cycle 398: RE-ARM the stand-down floor ──────────────────────
    // The episode is already live. Every fresh alarm-side reading advances
    // the arm timestamp, so after a re-dip the next in-range reading has to
    // be newer than the RE-DIP rather than merely newer than the original
    // crossing. Advance-only (`>`), so a replayed or backfilled older
    // alarm-side row can never move the floor backward.
    //
    // This does NOT open, close, re-fire or mutate anything else — it is
    // bookkeeping for a display predicate. Staleness is checked here for
    // the same reason the predicate checks it: a dead sensor's last
    // alarm-side value must not keep re-arming a floor forever.
    if (
      currentLevel != null &&
      ALARMING_LEVELS.has(currentLevel) &&
      readingTsMs != null &&
      !isStale(reading?.timestamp ?? null, Date.now(), reading?.provider) &&
      (armedReadingTsMs == null || readingTsMs > armedReadingTsMs)
    ) {
      setArmedReadingTsMs(readingTsMs);
    }

    // Cycle 66: the cycle-62 client-side worse-crossing re-arm is
    // **removed** (server-authored same-side mild→urgent escalation).
    // Cycle 82: the cycle-63 client-side recovery dwell is **removed**
    // too — recovery is decided by the engine and arrives as
    // `recovered_at` on the open row. While active, the trigger has
    // nothing left to do off the reading stream BEYOND the cycle-398
    // re-arm bookkeeping above.
    // Cycle 310: `socket.activeMemberId` in the deps — the creation guard
    // reads it, and a restored member context should re-evaluate the open
    // condition immediately (the member-change reset effect below already
    // handles the switch direction).
  }, [
    enabled,
    sgv,
    currentLevel,
    isActive,
    reset,
    socket.lastAlert,
    socket.activeMemberId,
    readingTsMs,
    armedReadingTsMs,
    reading?.timestamp,
    reading?.provider,
  ]);

  // ─── Cycle 61: late-joiner — open the local episode from the
  // cached server alert when it's in a post-Address state ──
  //
  // A device that connects (or wakes via cycle-33 wake-refresh) AFTER
  // Address was pressed receives the alert in `socket.lastAlert` (via
  // the connect snapshot seed in deliverable 3, or the next snooze /
  // silence / alert frame). The reading-stream trigger on that device
  // hasn't fired locally yet — but the episode is genuinely active
  // from the cross-device perspective and the collapsed bar must
  // render. Open the episode locally from server state when the
  // cached alert is **currently mid-episode**: snoozed_until is in
  // the future OR silenced_at is set on a recent fire. Don't open
  // from an ancient row sitting in cache — the reading-stream trigger
  // remains the primary "fresh alarm" path.
  useEffect(() => {
    if (!enabled || isActive) return;
    const a = socket.lastAlert;
    if (a == null) return;
    if (a.fired_at == null) return;
    if (!ALARMING_LEVELS.has(a.level)) return;
    // Cycle 82: never re-open a recovered episode from cache. A row
    // that carries `recovered_at` is terminal — the next crossing
    // opens a fresh row via the reading-stream trigger above.
    if (a.recovered_at != null) return;
    // Cycle 182: don't re-open a row we just cleared because the user
    // changed thresholds out of the alarming zone. The reading is
    // non-alarming under the new thresholds; the suppression is
    // released the moment it re-enters an alarming zone.
    if (a.id === thresholdClearedAlertIdRef.current) return;

    const nowMs = Date.now();
    const snoozeUntilMs = a.snoozed_until
      ? new Date(a.snoozed_until).getTime()
      : null;
    const silencedAtMs = a.silenced_at
      ? new Date(a.silenced_at).getTime()
      : null;

    // Mid-snooze window: snoozed_until is still in the future.
    const inSnoozeWindow =
      snoozeUntilMs != null && snoozeUntilMs > nowMs;
    // Recently silenced (within an hour — well past any plausible
    // recovery; the reading-stream rule will close the episode on
    // either device as soon as it observes recovery).
    const recentlySilenced =
      silencedAtMs != null && nowMs - silencedAtMs < 60 * 60 * 1000;

    if (!inSnoozeWindow && !recentlySilenced) return;

    setIsActive(true);
    setStartLevel(a.level);
    // Cycle 398: a late-joiner never observed the arming reading, so take
    // the arm timestamp from the ROW — `triggered_at` IS the timestamp of
    // the reading that fired it (see `evaluate_reading`, which copies
    // `reading.timestamp` into the column). That makes the stand-down floor
    // identical on a device that watched the crossing and one that adopted
    // the episode from cache, so the two cannot disagree about whether a
    // given in-range reading is new enough to clear the banner.
    setArmedReadingTsMs(
      (() => {
        const ms = new Date(a.triggered_at).getTime();
        return Number.isFinite(ms) ? ms : null;
      })(),
    );
    episodeStartedAtRef.current = Date.now();
    setLocalSnoozeUntil(null);
    setLocalSilenced(false);
    setOptimisticSnoozeUntil(null);
    setOptimisticSilenced(false);
    setAckFailure(null); // cycle 416: per-episode
    // Cycle 66: pending-silence is per-episode; reset for the new one.
    pendingSilenceRef.current = false;
  }, [enabled, isActive, socket.lastAlert]);

  // ─── Cycle 82: server-authored recovery → end the episode ─────────
  // Replaces the cycle-63 local reading-stream dwell. The engine
  // stamps `recovered_at` on the open row and broadcasts
  // `alert_recovered` (and the column rides the connect snapshot);
  // every device ends the episode the instant it sees that stamp, so
  // they clear TOGETHER instead of on independent local timers. We
  // family-match the recovered row against `startLevel` so a stale
  // older row carrying `recovered_at` can't tear down a fresh episode.
  useEffect(() => {
    if (!isActive) return;
    const a = socket.lastAlert;
    if (a == null || a.recovered_at == null) return;
    if (startLevel != null && !sameFamily(a.level, startLevel)) return;
    reset();
  }, [isActive, socket.lastAlert, startLevel, reset]);

  // ─── Cycle 435: A DEAD ROW CANNOT REMAIN THE ACTION TARGET ─────────────
  // The effect above waits for `recovered_at` on the SLOT. On 2026-09-14
  // 20:27Z the slot had moved to an open HIGH row while this device was
  // frozen, the urgent-low row it was bound to had recovered at 13:57, the
  // snapshot's `open_alerts` never lists a recovered row, and the
  // cycle-429 re-arm below correctly refuses to move a low-family episode
  // onto a high row — so the stamp this effect waits for could never
  // arrive, and the device posted a snooze against the dead row (409).
  //
  // The gap is closed from the other side: the socket keeps the server's
  // latest statement about every row per member (`serverRowTruth`), a
  // snapshot marks every low-family row it does not list as CLOSED, and an
  // episode whose bound row the server says is closed is torn down —
  // whatever its level. ⚠ It CLEARS client state only; the server recovers
  // rows, never this. The cycle-429 rule is untouched. If the live reading
  // is still alarming, the trigger effect above re-opens a fresh episode on
  // the row the server actually holds open.
  useEffect(() => {
    if (!isActive || alertId == null) return;
    const memberId = socket.activeMemberId;
    if (memberId == null) return;
    const truth = socket.byMember[memberId]?.serverRowTruth;
    if (episodeRowClosedByServer(alertId, truth)) reset();
  }, [isActive, alertId, socket.activeMemberId, socket.byMember, reset]);

  // ─── Cycle 182: clear a live episode when thresholds change ─────────
  // An ADDITIONAL recovery trigger, alongside (not replacing) the
  // cycle-82 server-authored recovery-dwell, the cycle-61/73 Snooze/Stop
  // state machine, the cross-device snooze sync, and the cycle-91
  // re-fire-after-recovery edge logic — all untouched.
  //
  // When the user edits thresholds such that the LIVE reading is no
  // longer in any alarming zone (e.g. lowers `target_low` below the
  // current reading), end the local episode/banner IMMEDIATELY instead
  // of waiting out the recovery-dwell window. `currentLevel` is already
  // `levelForReading(sgv, thresholds)` recomputed against the NEW
  // thresholds in this same render.
  //
  // Guardrails: clear ONLY when the reading now falls into a
  // non-alarming zone (`currentLevel == null`). NEVER clear while it is
  // still in ANY alarming zone under the new thresholds — that would
  // silence a real hypo/hyper. We clear only the user-facing LOCAL
  // episode; the server alert row stays as written (history) — we are
  // not retroactively recovering the server alert.
  const prevThresholdsRef = useRef(thresholds);
  useEffect(() => {
    const prev = prevThresholdsRef.current;
    prevThresholdsRef.current = thresholds;
    const changed =
      prev.urgent_low !== thresholds.urgent_low ||
      prev.target_low !== thresholds.target_low ||
      prev.target_high !== thresholds.target_high ||
      prev.urgent_high !== thresholds.urgent_high;
    if (!changed) return;
    if (!isActive || sgv == null) return;
    // Non-alarming under the NEW thresholds → clear now. Any alarming
    // level → leave the episode sounding (safety floor).
    if (currentLevel == null) {
      thresholdClearedAlertIdRef.current = socket.lastAlert?.id ?? alertId;
      reset();
    }
  }, [
    thresholds,
    isActive,
    sgv,
    currentLevel,
    reset,
    socket.lastAlert,
    alertId,
  ]);

  // Cycle 182: release the threshold-clear suppression the instant the
  // reading re-enters an alarming zone — a genuine hypo/hyper must
  // always be able to re-open and re-fire.
  useEffect(() => {
    if (currentLevel != null && ALARMING_LEVELS.has(currentLevel)) {
      thresholdClearedAlertIdRef.current = null;
    }
  }, [currentLevel]);

  // ─── Cycle 61: bind to socket.lastAlert as the synchronization key ──
  // The episode is active locally because the reading-stream trigger
  // opened it. The cached alert (socket.lastAlert) is the
  // authoritative cross-device episode state IFF its level matches —
  // it carries snoozed_until / silenced_at written by whichever
  // device pressed Address / Silence and broadcast back to all
  // sockets. We **don't** require a prior local latch on alertId: a
  // device that connects mid-episode (e.g. after the cycle-33 wake-
  // refresh) and seeds lastAlert from the connect snapshot must still
  // adopt the server state.
  const cachedAlert = socket.lastAlert;
  // Cycle 412: the number the alarm surfaces show — the crossing row's own
  // value while that row is newer than the reading this device holds (the
  // reading frame trails the alert frame by the fan-out, ~2 s). Display
  // only: `currentLevel`, the stand-down and the audio bridge keep reading
  // the LIVE reading above.
  const displaySgv = alarmDisplaySgv(sgv, readingTsMs, cachedAlert);
  const cachedAlertIsCurrent =
    cachedAlert != null &&
    cachedAlert.fired_at != null &&
    ALARMING_LEVELS.has(cachedAlert.level) &&
    // Cycle 82: a recovered row is terminal — it is no longer the
    // "current" episode for snooze/silence adoption or alertId
    // binding. The dedicated recovery effect below resets the local
    // episode when it sees `recovered_at`.
    cachedAlert.recovered_at == null &&
    // Cycle 63: family-match (was: exact level match). The cycle-61
    // exact-match gate broke when the reading oscillated across the
    // low ↔ urgent_low boundary (or high ↔ urgent_high) — the
    // cached alert's level would lag behind `startLevel` for one
    // render and the Stop control would vanish. The family-match
    // bind is still strict enough to refuse a stale row from a
    // different *prior* episode (which would have closed via
    // recovery before re-opening at this level).
    (startLevel == null || sameFamily(cachedAlert.level, startLevel));

  // Late-binding: as soon as the cached alert is the current one,
  // remember its id for the POST surface (`address()` / `silence()`)
  // and clear the optimistic flags — the server is now authoritative.
  useEffect(() => {
    if (!cachedAlertIsCurrent || cachedAlert == null) return;
    if (alertId === cachedAlert.id) return;
    setAlertId(cachedAlert.id);
    setOptimisticSnoozeUntil(null);
    setOptimisticSilenced(false);
    setOptimisticTreatingUntil(null);
    // ⚠ Cycle 416 (safety review): clear the failure notice too. This effect
    // fires when a worsening inserts a NEW row mid-episode; a "· NOT SNOOZED"
    // stamped against the previous row must not ride onto the new one, where
    // it would suppress that row's own calm state. It errs loud either way —
    // the new row sounds regardless — but the notice must describe the row
    // the user is looking at.
    setAckFailure(null);
  }, [cachedAlertIsCurrent, cachedAlert?.id, alertId]);

  // ─── Cycle 429: THE SLOT FOLLOWS SERVER TRUTH, ACROSS FAMILIES ─────────
  // The late-binding effect above adopts a server row only when it is the
  // SAME family as `startLevel` (the cycle-63 family match, which refuses a
  // stale row from a prior episode). That left one hole open on
  // 2026-09-10: a phone whose episode had latched on a HIGH row (its last
  // live frame, hours earlier) came back to a live URGENT LOW — the
  // takeover rendered from the fresh reading, `episodeLevel` said
  // urgent_low, and every tap went to the remembered high row. When the
  // server's slot (`socket.lastAlert`, now seeded from server rows only)
  // holds an OPEN alarming row of a DIFFERENT family from the local
  // episode, the local episode is the stale one: re-arm it on the server's
  // row. Loud direction only — it binds to an open row, never clears one.
  useEffect(() => {
    if (!isActive) return;
    const a = socket.lastAlert;
    if (a == null || a.fired_at == null) return;
    if (!ALARMING_LEVELS.has(a.level)) return;
    if (a.recovered_at != null || a.silenced_at != null) return;
    if (startLevel == null || sameFamily(a.level, startLevel)) return;
    // Loud direction ONLY, and pinned as such: a LOW-family episode is
    // never re-armed onto a high row — the slot rule already prefers the
    // low row, and a live high frame that briefly takes the slot must not
    // move a sounding low episode off its row (safety review, finding 3).
    if (sameFamily(startLevel, "urgent_low") && !sameFamily(a.level, "urgent_low")) {
      return;
    }
    setStartLevel(a.level);
    setAlertId(a.id);
    setArmedReadingTsMs(
      (() => {
        const ms = new Date(a.triggered_at).getTime();
        return Number.isFinite(ms) ? ms : null;
      })(),
    );
    setOptimisticSnoozeUntil(null);
    setOptimisticSilenced(false);
    setOptimisticTreatingUntil(null);
    setAckFailure(null);
  }, [isActive, startLevel, socket.lastAlert]);

  // Cycle 67 — root-cause fix for the cycle-66 dead-Stop bug.
  //
  // Intent (cycle 66): drop optimistic / local silence when the
  // server-authored `silenced_at` clears (the engine-authored
  // mild→urgent re-arm), so audio re-arms together with the rest of
  // the household. The alert id stays the same across the
  // escalation (one-row-per-episode), so the alertId-change effect
  // above never fires for the re-arm — this effect is the one that
  // has to notice it.
  //
  // Cycle-66 implementation was wrong: it cleared the optimistic /
  // local silence whenever `silenced_at` was null, not only on the
  // transition from non-null → null. The result was that the
  // instant the user pressed Stop and `setOptimisticSilenced(true)`
  // ran, the cached alert's `silenced_at` was still null (the POST
  // hadn't returned yet) and this effect immediately reset the
  // optimistic flag back to false — the audio gate
  // (`audioOn = phase === "active" && !silenced`) kept `audioOn`
  // true and the loop never stopped. Stop appeared dead.
  //
  // Fix: track the previous server-authored `silenced_at` and only
  // clear local mute on the **transition** from non-null → null.
  // Steady-state `null` (pre-Stop / pre-POST) is no-op. The receiving
  // device path is unchanged — its `serverSilenced` already drives
  // `silenced` directly without optimistic state.
  const prevServerSilencedAtRef = useRef<string | null>(null);
  const prevServerAlertIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!cachedAlertIsCurrent || cachedAlert == null) {
      prevServerSilencedAtRef.current = null;
      prevServerAlertIdRef.current = null;
      return;
    }
    // Fresh alert id (new episode) — seed the tracker and skip the
    // transition check; the alertId-change effect above already
    // cleared optimistic state for the new episode.
    if (prevServerAlertIdRef.current !== cachedAlert.id) {
      prevServerAlertIdRef.current = cachedAlert.id;
      prevServerSilencedAtRef.current = cachedAlert.silenced_at ?? null;
      return;
    }
    const prev = prevServerSilencedAtRef.current;
    const curr = cachedAlert.silenced_at ?? null;
    prevServerSilencedAtRef.current = curr;
    // Only fire on the engine re-arm transition (was set, now cleared).
    if (prev != null && curr == null) {
      setOptimisticSilenced(false);
      setLocalSilenced(false);
    }
  }, [cachedAlertIsCurrent, cachedAlert?.silenced_at, cachedAlert?.id]);

  // Cycle 66: fire a queued silence POST as soon as an `alertId`
  // lands. Stop pressed before the first heartbeat / snapshot leaves
  // `pendingSilenceRef.current === true`; once binding happens the
  // POST goes out and other devices converge. Optimistic local mute
  // is already in place from the gesture.
  useEffect(() => {
    if (!alertId) return;
    if (!pendingSilenceRef.current) return;
    if (synthetic) {
      pendingSilenceRef.current = false;
      return;
    }
    pendingSilenceRef.current = false;
    void (async () => {
      {
        // Cycle 416: retried on a fault with no answer, then SURFACED. The
        // cycle-409/410 audio rules below are unchanged; only the silence
        // about the failure is gone.
        const r = await runAck("stop", () => alertsApi.silence(alertId));
        if (!r.ok && r.error.kind !== "network") {
          // Cycle 410: the SERVER refused — `409 not_silenceable_urgent_low`
          // on a stale client, a 404 — so there is no silence. Withdraw the
          // optimistic mute and sound again; a network fault keeps it (the
          // cycle-409 snooze-door rule, in the loud direction).
          setOptimisticSilenced(false);
          setLocalSilenced(false);
          // Cycle 412: a Stop addressed to a RECOVERED row is a stale slot —
          // re-seed from server truth now, not at the 90 s tick.
          if (treatingErrorCode(r.error) === "episode_closed") {
            setReconcileNonce((n) => n + 1);
          }
        }
      }
    })();
    // ⚠ Cycle 416: the swallowed `catch {}` that used to end this block is
    // GONE — the local mute is kept on a network fault (cycle 409) — it is simply
    // no longer silent about it.
  }, [alertId, synthetic, runAck]);

  // Server-sourced episode fields. No alertId-equality gate (cycle
  // 61 fix): the level-match condition above is sufficient because
  // the socket cache is already member-scoped (cycle 24 multiplex)
  // and the cycle-60 audience fan-out delivers snooze/silence
  // broadcasts to every device entitled to the member.
  const serverSourceAlert = cachedAlertIsCurrent ? cachedAlert : null;
  let serverSnoozeUntilMs: number | null = null;
  let serverSilenced = false;
  // Cycle 391: server-authored treating window, read on the SAME footing
  // as the snooze above — no alertId-equality gate (the cycle-61 fix).
  let serverTreatingUntilMs: number | null = null;
  let serverTreatingCount = 0;
  if (serverSourceAlert) {
    if (serverSourceAlert.snoozed_until) {
      serverSnoozeUntilMs = new Date(
        serverSourceAlert.snoozed_until,
      ).getTime();
    }
    if (serverSourceAlert.silenced_at != null) {
      serverSilenced = true;
    }
    if (serverSourceAlert.custody_until) {
      serverTreatingUntilMs = new Date(
        serverSourceAlert.custody_until,
      ).getTime();
    }
    serverTreatingCount = serverSourceAlert.custody_count ?? 0;
  }

  // Pick the effective snooze-until: prefer server, then optimistic,
  // then local-only fallback. The optimistic value masks the round-trip
  // between gesture and next WS frame.
  const snoozeUntilMs =
    serverSnoozeUntilMs ?? optimisticSnoozeUntil ?? localSnoozeUntil;
  const silenced =
    serverSilenced || optimisticSilenced || localSilenced;

  // ─── Cycle 391: the treating window ────────────────────────────────
  // SERVER TRUTH WINS OUTRIGHT once a row for this episode carries any
  // treating state — including a TEARDOWN. That asymmetry is the safety
  // property: `?? optimistic` (the shape the snooze uses) would let a
  // stale optimistic value keep a device quiet for the rest of its
  // window after a worsening breakthrough had already cleared the row.
  // A breakthrough must re-arm every device instantly, so a cleared row
  // has to beat local optimism, not fall back to it.
  const treatingUntilMs =
    serverSourceAlert != null &&
    (serverSourceAlert.custody_until != null ||
      (serverSourceAlert.custody_count ?? 0) > 0)
      ? serverTreatingUntilMs
      : optimisticTreatingUntil;
  const treatingRemainingMs =
    treatingUntilMs != null
      ? Math.max(0, treatingUntilMs - Date.now())
      : null;
  // ⚠ CYCLE 393: `treating` MEANS **DECLARED** CUSTODY, NOT ANY CUSTODY.
  //
  // Snooze is a custody door now, so `custody_until` is set for a snooze
  // too. Without this gate a snooze would light up the whole treating
  // surface — the "Treating M:SS" banner, the sheet, and (worse) the
  // `audioOn = … && !treating` term, which is fine for a snooze but would
  // be reached by the wrong reasoning. (Cycle 409: a snooze on an urgent
  // low now shows the timer screen via `quietWindow` — a display twin
  // that gates nothing; `treating` is still what the audio gate reads.)
  //
  // Local optimism counts as DECLARED because the only thing that sets it
  // is this device's own treating tap; a snooze never takes that path.
  const custodyKind: "declared" | "quiet" | null =
    serverSourceAlert?.custody_kind ??
    (optimisticTreatingUntil != null ? "declared" : null);
  const treating =
    isActive &&
    treatingRemainingMs != null &&
    treatingRemainingMs > 0 &&
    custodyKind === "declared";
  // Cycle 409: the quiet twin. Server-sourced only (a snooze never sets
  // local treating optimism), and it can only ever SHOW a window the
  // server holds — it gates no sound and no rail.
  const quietWindow =
    isActive &&
    treatingRemainingMs != null &&
    treatingRemainingMs > 0 &&
    custodyKind === "quiet";
  const treatingCount = serverTreatingCount;

  // ─── Cycle 410: the lead window (audio only) ──────────────────────────
  // ⚠ THE LEAD WINDOW SUPPRESSES LOCAL ALARM AUDIO AND NOTHING ELSE. IT
  // NEVER MAKES ANYTHING QUIETER THAN IT ALREADY WAS. `audioOn` above is
  // untouched — it is still the cycle-63/81 audio↔control gate every
  // surface reads. What the window changes is WHICH row's audio plays
  // (`audibleLevel`), through the pure descent rule, and only while the
  // BOUND row's window is open on this device's own clock:
  //
  //   * the deadline is `min(server audio_lead_until, first receipt + 90s)`
  //     — the receipt-anchored cap lives in the socket's `leadAnchors`, so
  //     a far-future or replayed value can never hold audio past ninety
  //     seconds from the frame that announced it;
  //   * a frame carrying `null` drops the anchor: the end signal;
  //   * custody of either kind (`treating` / `quietWindow`) ends it — the
  //     server closes the window on the same transaction, and locally the
  //     custody surfaces already own the quiet;
  //   * "Sound the alarm now" ends it on THIS device at the tap.
  //
  // ⚠ THIS NEVER MOVES THE SLOT. `serverSourceAlert` — cycle 408's
  // `preferSoundingRow` choice — stays the bound row and every POST target;
  // `selectAudibleRow` only picks whose audio plays.
  const memberStream = socket.activeMemberId
    ? socket.byMember[socket.activeMemberId]
    : undefined;
  const leadNowMs = Date.now();
  const boundLeadAnchor =
    serverSourceAlert != null
      ? memberStream?.leadAnchors[serverSourceAlert.id]
      : undefined;
  const audioLeadUntil =
    isActive &&
    !treating &&
    !quietWindow &&
    !silenced &&
    serverSourceAlert != null &&
    serverSourceAlert.level === "urgent_low" &&
    leadOverriddenAlertId !== serverSourceAlert.id
      ? effectiveLeadDeadline(boundLeadAnchor, leadNowMs)
      : null;
  const audioLead = audioLeadUntil != null;
  const audioLeadRemainingMs =
    audioLeadUntil != null ? Math.max(0, audioLeadUntil - leadNowMs) : null;
  // ─── Cycle 410.1: the PENDING GRACE ──────────────────────────────────
  // ⚠ THE ONLY OPTIMISTIC SILENCE IN THE SYSTEM. The crossing frame said a
  // window is eligible and the SID is not back yet: hold the URGENT-LOW
  // audio for at most 3 s from the frame's receipt (`effectivePendingDeadline`
  // caps it locally whatever the timestamp says). Resolved by the next
  // frame for the row — a window (defer) or nothing (LOUD) — or by the
  // 1 Hz tick reaching the deadline. Custody, a silence, the override and
  // an open window all make it moot; a reconnect snapshot never carries it.
  const audioLeadPendingUntil =
    !audioLead &&
    isActive &&
    !treating &&
    !quietWindow &&
    !silenced &&
    serverSourceAlert != null &&
    serverSourceAlert.level === "urgent_low" &&
    leadOverriddenAlertId !== serverSourceAlert.id
      ? effectivePendingDeadline(
          memberStream?.pendingAnchors[serverSourceAlert.id],
          leadNowMs,
        )
      : null;
  const audioLeadPending = audioLeadPendingUntil != null;
  // ─── Cycle 424: the ascent latch ─────────────────────────────────────
  // The server creates no low row and pages no low rail while the member
  // climbs out of an urgent low; this device mirrors that for the one
  // thing it owns — the cue. A low-band reading under a latching urgent
  // row keeps the URGENT row's audio (the kiosk's "audible cue until
  // acceptance" floor is satisfied by the urgent row) instead of switching
  // to the low loop. Derived, never stored: the bound row's own columns.
  const ascentLatched = lowRungLatched(serverSourceAlert, currentLevel);
  let audibleLevel: AlertLevel | null = ascentLatched
    ? "urgent_low"
    : currentLevel;
  if (audioLead || audioLeadPending) {
    // Under a window OR the pending grace the bound urgent row is held;
    // the descent rule still finds a sounding `low` sibling.
    const candidates: AudibleCandidate[] = Object.values(
      memberStream?.alertRows ?? {},
    ).map((alert) => {
      const lead = effectiveLeadDeadline(memberStream?.leadAnchors[alert.id], leadNowMs);
      const pending = effectivePendingDeadline(
        memberStream?.pendingAnchors[alert.id],
        leadNowMs,
      );
      const held =
        lead != null && pending != null ? Math.max(lead, pending) : (lead ?? pending);
      // Cycle 427: the dark-feed tone rule needs the LOCAL stale check too
      // (`noDataQuietHolds` is a belt on the server flag, never the flag
      // alone) — computed here from the same reading `readingStale` below
      // reads, so the candidate rule and the bound-row rule cannot disagree.
      const stale = isStale(
        socket.lastReading?.timestamp ?? null,
        leadNowMs,
        socket.lastReading?.provider,
      );
      return { alert, leadUntilMs: held, readingStale: stale };
    });
    audibleLevel = selectAudibleRow(candidates, leadNowMs)?.level ?? null;
  }

  // ─── Snooze countdown (1 Hz tick) ────────────────────────────────
  useEffect(() => {
    // Cycle 391: the same 1 Hz tick drives the treating countdown, which
    // is computed from the SERVER wall clock on every device — so the
    // kiosk banner and every phone read the same M:SS rather than each
    // running its own timer from its own tap moment.
    // Cycle 410: the lead-window countdown ticks on the same clock, and
    // the same tick is what makes the dead-man deadline FIRE — the render
    // after the deadline derives `audioLead = false` and the audio bridge
    // sounds, with or without a socket.
    const candidates = [
      snoozeUntilMs,
      treatingUntilMs,
      audioLeadUntil,
      // Cycle 410.1: the pending grace's own deadline — the render at ≤3 s
      // is what makes the dead-man switch fire without a frame.
      audioLeadPendingUntil,
    ].filter((v): v is number => v != null);
    const deadline = candidates.length ? Math.max(...candidates) : null;
    if (deadline == null) return;
    if (deadline <= Date.now()) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [snoozeUntilMs, treatingUntilMs, audioLeadUntil, audioLeadPendingUntil]);

  // ─── Active member change → end episode ──────────────────────────
  const prevActiveRef = useRef<string | null>(null);
  useEffect(() => {
    const cur = socket.activeMemberId;
    if (prevActiveRef.current !== cur) {
      prevActiveRef.current = cur;
      if (isActive) reset();
    }
  }, [socket.activeMemberId, isActive, reset]);

  // ─── Derived phase + countdown ─────────────────────────────────────
  let phase: EpisodePhase = "idle";
  let snoozeRemainingMs: number | null = null;
  if (isActive) {
    if (snoozeUntilMs != null) {
      const remaining = snoozeUntilMs - Date.now();
      if (remaining > 0) {
        phase = "snoozed";
        snoozeRemainingMs = remaining;
      } else {
        // Snooze elapsed; the episode is active again. The UI shows
        // the Silence button (hasSnoozed === true).
        phase = "active";
      }
    } else {
      phase = "active";
    }
  }

  const hasSnoozed = snoozeUntilMs != null;
  // Cycle 66: `stopped` is now an alias for the shared cross-device
  // `silenced` signal — they're the same thing. The user-facing
  // "Stop" button POSTs /silence and sets `silenced_at`; receiving
  // devices see `silenced=true` via the same path the pressing
  // device does. Audio goes quiet whenever silenced is true.
  const stopped = silenced;
  // Cycle 391: an open treating window stops the loop through the SAME
  // gate everything else uses. Factoring it in here rather than adding a
  // second mute path means the existing `useAlarmSounds` episode bridge
  // stops naturally on the falling edge (cycle 312's hard-cut fires for
  // free) and restarts the CORRECT zone loop when the window ends —
  // including the instant a breakthrough tears it down.
  // ─── Cycle 81: phase-live control gate ─────────────────────────────
  // `isLive` is the one predicate every control surface gates on
  // (replacing `alarmActive`, which tracks the live reading's zone and went
  // false the instant the reading climbed back across the threshold
  // mid-episode — the recovery-dwell control gap).
  //
  // Cycle 398 moved this ABOVE `audioOn` (it used to sit below) because the
  // stand-down predicate needs it and `audioOn` is now DERIVED FROM the
  // stand-down predicate. The value is byte-identical; only the position
  // moved.
  const isLive = phase === "active" || phase === "snoozed";

  // ─── Cycle 63 alarm-active floor: the live reading's zone ───────────
  // Also moved up in cycle 398 (`readingStale` is an input to the
  // stand-down predicate). `alarmActive` itself still sits below, because
  // it reads `audioOn`.
  const latestReading = socket.lastReading;
  const readingStale = isStale(
    latestReading?.timestamp ?? null,
    Date.now(),
    latestReading?.provider,
  );
  const readingInAlarmZone =
    currentLevel != null &&
    ALARMING_LEVELS.has(currentLevel) &&
    !readingStale;
  // ─── Cycle 427: the dark-feed tone rule (operator-authorized 2026-09-08) ─
  // The SERVER flags the bound row (`no_data_quiet`: an urgent-low row open,
  // no window, the feed stale); THIS device honours it only while it can
  // see the reading is stale on its own clock and no window is open — so a
  // bogus flag can never quiet a live feed, and the FIRST fresh reading
  // (a reading frame, before any server frame) brings the tone back at
  // once. AUDIO SELECTION ONLY, the same seam as the lead window: `audioOn`
  // and every control are untouched, the takeover stays with the number
  // greyed, the calls and pushes continue server-side.
  const noDataQuiet =
    isActive &&
    !treating &&
    !quietWindow &&
    serverSourceAlert != null &&
    noDataQuietHolds(serverSourceAlert, readingStale, Date.now());
  if (noDataQuiet) audibleLevel = null;

  // ─── Cycle 304 → 398: the in-range DISPLAY stand-down ───────────────
  // Cycle 304 hid a MUTED episode's banner on the first fresh in-range
  // reading. Cycle 398 widens it to a live episode in ANY phase — the
  // operator report is that an ACTIVE Low / Urgent Low / High / Urgent High
  // banner sat on the tablet and the phone for minutes after the household
  // was back in range, because the only thing that could clear it was the
  // server's 90 s recovery dwell (a SECOND reading on a 5-minute cadence).
  //
  // ⚠ THE CYCLE-81 FLOOR IS NOT WEAKENED — IT IS RE-DERIVED FROM THE OTHER
  // SIDE, AND IT IS THE SAME PROPOSITION. Cycle 304 guaranteed
  // `audioOn ⇒ ¬suppressed` by construction (an active episode could never
  // satisfy the predicate). Cycle 398 guarantees `suppressed ⇒ ¬audioOn` by
  // computing the predicate FIRST and deriving `audioOn` from it, just
  // below. Both statements are `¬(audioOn ∧ suppressed)`. The audio now
  // stops in the SAME RENDER the banner hides, so there is no frame in
  // which the loop can sound with no control on screen.
  //
  // DISPLAY ONLY. The episode is not ended, cleared, reset or acknowledged;
  // `phase`, the snooze countdown, `startLevel`, the bound row and the
  // server's authority over `recovered_at` are untouched. The next
  // alarm-side reading flips it false and everything returns in one render.
  //
  // Thresholds are re-derived every render (`currentLevel` is
  // `levelForReading(sgv, thresholds)` against the CURRENT thresholds), so
  // a Settings edit that puts the newest reading in range clears the banner
  // in the same render — the "even after the threshold changes" half of the
  // report, free, with no latch to go stale.
  const bannerSuppressedInRange = inRangeStandDown({
    isLive,
    synthetic,
    hasActiveMember: socket.activeMemberId != null,
    hasReading: latestReading != null && sgv != null,
    readingLevel: currentLevel,
    readingFresh: !readingStale,
    readingTsMs,
    armedReadingTsMs,
  });
  // Cycle 398: a stable one-shot key for consumers that need the EDGE (the
  // in-range cue, telemetry) rather than each writing its own detector.
  const inRangeStandDownAt = bannerSuppressedInRange ? readingTsMs : null;

  // ⚠ CYCLE 398: `audioOn` IS DERIVED FROM THE STAND-DOWN, NOT THE OTHER
  // WAY ROUND. `!treating` is the cycle-391 term and is KEPT — dropping it
  // would restart the alarm loop over an open treating window. See
  // `lib/alarmStandDown.ts`.
  const audioOn = audioOnFor({
    phase,
    silenced,
    treating,
    standDown: bannerSuppressedInRange,
  });

  // Cycle 410: `audible` is the one lead-window value that needs
  // `audioOn` — everything else is derived above the 1 Hz tick (which
  // needs the deadline). Is anything actually sounding for this member.
  const audible = audioOn && audibleLevel != null;

  // ─── Cycle 310/342: server-truth reconciliation for LATCHED episodes ─
  // The two-unit fleet wedge: an episode's ONLY end paths were the
  // `recovered_at` mirror on `socket.lastAlert` (a WS delivery) and the
  // threshold-change clear — so a device that missed the recovery frame
  // (dead/zombie WS while the native heartbeat kept flowing) or whose
  // account had a NULL active-member pointer (which also gates the
  // wake-refresh REST reseed) held `alarmActive` forever and the OTA
  // updater deferred its staged install indefinitely. While an episode
  // is latched, poll the consumer-scoped `GET /api/alerts/active_count`;
  // when the SERVER says zero fired, un-recovered, non-synthetic alerts
  // exist across this account's accessible members, the latch is stale
  // — clear it and report the wedge signature. STRICTLY a server-truth
  // MIRROR: the SPA never declares recovery on its own (an open row
  // anywhere keeps the episode), an unreachable server keeps deferring,
  // and the first check waits out the server's 90 s recovery dwell so
  // the normal `alert_recovered` path always wins on healthy devices.
  //
  // Cycle 342 (root-caused a phantom urgent_low that looped through an
  // entire deploy-forced reload/reconnect with no server-side alert row
  // ever created — traced to a race in the reading-stream OPEN trigger
  // above, which can latch an episode off a transient client-side
  // misread with no corroborating server alert): the original `audioOn`
  // exclusion assumed "a sounding alarm always implies an open row
  // server-side" — TRUE for a real alarm (the same reading that trips
  // the local zone check also reaches the edge-triggered engine), but
  // FALSE for a phantom one by construction, which is exactly the case
  // that needs unwedging. The exclusion is REMOVED — this reconciliation
  // now also runs while sounding, on the SAME 90 s-first/60 s-thereafter
  // cadence. This cannot silence a real alarm: a real low/urgent_low
  // keeps `active_count > 0` server-side for as long as it's genuinely
  // open (recovery itself needs the same 90 s in-range dwell this check
  // already waits out before its first look), so the clear only ever
  // fires when the server affirmatively confirms nothing is alarming
  // anywhere on the account — the definition of "not real". Clearing
  // `isActive` flips `phase` to "idle" on the same render, which the
  // `useAlarmSounds` bridge picks up via `episode.audioOn` and stops the
  // loop through the existing plumbing — no separate audio-stop needed
  // here. The "lows kill" floor (Snooze-only, no Stop, never bypassed by
  // an install/restart gate) is untouched — this is a *server-verified*
  // clear, not a UI dismissal.
  const activeMemberNullRef = useRef(socket.activeMemberId == null);
  activeMemberNullRef.current = socket.activeMemberId == null;
  const onWedgeClearedRef = useRef(onWedgeCleared);
  onWedgeClearedRef.current = onWedgeCleared;
  useEffect(() => {
    if (!enabled || !isActive || synthetic) return;
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      const r = await alertsApi.activeCount();
      if (cancelled || !r.ok) return; // server unreachable → keep latched
      if (r.data.active_alerts > 0) return; // genuinely live somewhere
      const startedAt = episodeStartedAtRef.current;
      const episodeAgeSeconds =
        startedAt != null
          ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
          : 0;
      onWedgeClearedRef.current?.({
        episodeAgeSeconds,
        activeMemberNull: activeMemberNullRef.current,
      });
      reset();
    };
    // Cycle 412: an `episode_closed` refusal bumps `reconcileNonce`, and the
    // FIRST check runs at once instead of at 90 s. The nonce is CONSUMED
    // here (safety review F5) — otherwise every later re-run of this effect,
    // including a brand-new episode's mount, would skip the cycle-342 first
    // delay for the life of the component.
    const immediate = reconcileNonce > consumedReconcileNonceRef.current;
    if (immediate) consumedReconcileNonceRef.current = reconcileNonce;
    const first = window.setTimeout(
      check,
      immediate ? 0 : SERVER_TRUTH_RECONCILE_FIRST_MS,
    );
    const id = window.setInterval(check, SERVER_TRUTH_RECONCILE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(id);
    };
  }, [enabled, isActive, synthetic, reset, reconcileNonce]);

  // Cycle 63 alarm-active floor: true whenever alarm audio is
  // looping OR the current live reading sits in an alarming zone
  // (per the user's live cycle-35 thresholds) and is not stale.
  // Does NOT depend on phase / alertId / lastAlert.level — that's
  // the whole point. The audio bridge in App.tsx and the
  // AlarmSilenceGuard read this same predicate so the audio loop
  // and the Stop control share one source of truth: control on
  // screen iff audio is (or could be) looping.
  // (Cycle 398: `latestReading` / `readingStale` / `readingInAlarmZone` /
  // `isLive` are computed above now — the stand-down predicate needs them
  // before `audioOn`. Their values are unchanged.)
  const alarmActive = audioOn || readingInAlarmZone;

  // The effective episode level for deciding which surface renders.
  // `currentLevel` (the live reading zone) wins when set — that's how
  // the engine-authored same-side mild→urgent re-arm flips the
  // surface to the urgent takeover. When the reading has recovered
  // into range during the dwell (`currentLevel` null) but the episode
  // is still live, fall back to `startLevel` so the matching banner
  // stays mounted with a control instead of going empty. Idle =>
  // `startLevel` is null => this is just `currentLevel`.
  // Cycle 408: the server-bound row's level joins the derivation (same
  // family, more severe wins) — see `lib/episodeLevel.ts` for why a
  // reading-only derivation defeats the cycle-73 floor on a cold start.
  const boundLevel = serverSourceAlert?.level ?? null;
  const episodeLevel = effectiveEpisodeLevel(
    currentLevel,
    startLevel,
    boundLevel,
  );

  // Cycle 428 — the Stage-6 header rule (text only). Pure and clock-free in
  // `lib/alarmStandDown.ts` so vitest pins it without a renderer.
  const recoveringFromUrgentLow = recoveringFromUrgentLowFor({
    isLive,
    episodeLevel,
    sgv,
    readingFresh: !readingStale,
    urgentLow: thresholds.urgent_low,
  });

  // ⚠ CYCLE 416 — `retry` EXISTS BECAUSE THE RETRY BUTTON WAS INERT WITHOUT IT.
  // `phase !== "active"` is the right guard for a TAP: it stops a snooze being
  // posted for an episode that is already snoozed. But on a network fault the
  // cycle-409 rule KEEPS `optimisticSnoozeUntil`, which makes `phase` read
  // "snoozed" for the whole 5-minute window — so the very failure the notice
  // reports also disabled its own Retry, and on an urgent low (no Stop) that
  // left NO working control at all. A retry is not a fresh tap: the user is
  // re-sending an ack the server never confirmed, and the optimistic value
  // that blocks it is OUR OWN, not the server's. `isActive` still applies —
  // a retry on a dead episode is refused.
  const address = useCallback(async (opts?: { retry?: boolean }) => {
    if (!isActive) return;
    if (!opts?.retry && phase !== "active") return;
    // Cycle 454 (operator decision): High and Urgent High are Stop-only.
    // No surface offers Snooze on a high any more; this is the hook's floor
    // (the cycle-73 shape, mirrored) so a stray caller cannot silence a high
    // on a snooze either — and no optimistic silence is taken first. The
    // server refuses the SPA's high snooze too (409 `not_snoozable_high`).
    if (
      !synthetic &&
      (episodeLevelRef.current === "high" ||
        episodeLevelRef.current === "urgent_high")
    ) {
      return;
    }
    // Optimistic update first so the local user doesn't sit listening
    // to the alarm while the round-trip completes.
    // ⚠ FIVE MINUTES, ON EVERY LEVEL — DELIBERATELY NOT the ten-minute
    // window the server grants an urgent-low snooze (cycle 409). This
    // value is the only silence this device holds with NO server window
    // behind it: a POST lost to the network keeps it (below), and a null
    // server `snoozed_until` falls through to it. Mirroring the server's
    // grant here would have DOUBLED that ungoverned silence on the most
    // dangerous level (the final re-review's MEDIUM). The countdown reads
    // 5:00 for the round trip and then server truth — never more than the
    // server will grant, so a lost frame can only lapse early, never late.
    setOptimisticSnoozeUntil(Date.now() + SNOOZE_MS);
    if (!alertId || synthetic) return;
    {
      // Cycle 416: retried on a fault with no answer, and SURFACED instead
      // of swallowed. The cycle-409 audio rules below are unchanged.
      // Default 5-minute duration. Server clamps to [60, 1800].
      const r = await runAck("snooze", () =>
        alertsApi.snooze(alertId, SNOOZE_MS / 1000),
      );
      if (!r.ok) {
        // Cycle 409 (re-review): a refusal the SERVER answered — the quiet
        // door's 429, an auth fault, a 404 — means there is no snooze, so
        // the optimistic silence is withdrawn and this device sounds
        // again. A network fault keeps it: the local user is at least not
        // stuck on the alarm, and the next reading or WS frame reconciles.
        if (r.error.kind !== "network") setOptimisticSnoozeUntil(null);
        if (treatingErrorCode(r.error) === "episode_closed") {
          setReconcileNonce((n) => n + 1);
        }
        return;
      }
      if (r.data.snoozed_until == null) {
        // Cycle 409 (final re-review): the server answered and holds NO
        // snooze — a snooze during a DECLARATION writes nothing (the
        // declaration holds the silence; the treating screen this device
        // already shows keeps the quiet). Withdraw the optimistic value
        // rather than let a countdown the server never granted outlive the
        // window.
        setOptimisticSnoozeUntil(null);
        return;
      }
      // Successful response is identical to the WS frame body that
      // will arrive moments later. The WS frame upserts into
      // socket.lastAlert; the optimistic clear-on-id-change effect
      // above will then take over.
    }
  }, [isActive, phase, alertId, synthetic, runAck]);

  // ─── Cycle 391: "I'm treating this" ────────────────────────────────
  // Ref mirrors so `treat` stays a stable callback (the surfaces pass it
  // straight into onClick) while still reading the CURRENT episode level,
  // alert id and engagement count at tap time.
  const episodeLevelRef = useRef<AlertLevel | null>(episodeLevel);
  episodeLevelRef.current = episodeLevel;
  // Cycle 408: the level of the row this device is BOUND to, read from
  // the server's own row rather than re-derived from the live reading.
  const boundLevelRef = useRef<AlertLevel | null>(serverSourceAlert?.level ?? null);
  boundLevelRef.current = serverSourceAlert?.level ?? null;
  const alertIdRef = useRef<string | null>(alertId);
  alertIdRef.current = alertId;

  // Cycle 456: the window this device's own successful tap opened.
  const lastTreatWindowRef = useRef<string | null>(null);
  const lastTreatWindow = useCallback(() => lastTreatWindowRef.current, []);

  const treat = useCallback(
    async (): Promise<string | null> => {
      if (!isActive) return "not_active";
      // Urgent low ONLY, mirrored client-side so no surface can offer the
      // control where the server would refuse it. The server gate is the
      // real one; this keeps the two from disagreeing on screen.
      //
      // ⚠ CYCLE 408: EITHER SIGNAL IS ENOUGH TO SEND. `episodeLevel` is
      // re-derived from the live reading against the thresholds this
      // device currently holds — which are `DEFAULT_THRESHOLDS` for the
      // first moments after a cold start from a critical alert, before
      // the member's settings load. A phone opened onto a real urgent low
      // could therefore classify the same reading as `low` and refuse
      // the tap locally, with the server never consulted. The SERVER's
      // own row says what the episode is; if it says `urgent_low`, send,
      // and let the real gate decide.
      if (
        episodeLevelRef.current !== "urgent_low" &&
        boundLevelRef.current !== "urgent_low"
      ) {
        return "not_urgent_low";
      }
      // Optimistic first, so the room stops hearing the siren on the tap
      // rather than after a round-trip. Capped at the client's own view
      // of the engagement rule so the optimistic countdown can't briefly
      // promise 20 minutes the server is about to cut to 5.
      // ⚠ RESOLVE THE SERVER ROW **BEFORE** ANY OPTIMISM. A treating
      // window is only safe because the SERVER can break it — on a
      // worsening reading, on a stale feed, at expiry — and it does that
      // by broadcasting a teardown for a row it opened. An optimistic
      // window with no row behind it is therefore unbreakable by every
      // one of those rules.
      //
      // The worst case is exactly the case the stale-feed REFUSAL exists
      // for: on a dark sensor there are no new readings, so no heartbeat
      // ever lands an `alertId`, so the POST that would have been refused
      // with `no_fresh_reading` is never sent — and a purely local window
      // would quiet this device instead of keeping the alarm going. That
      // inverts the defense.
      //
      // So: no id, no window, and say so. This deliberately does NOT
      // follow the cycle-66 `pendingSilenceRef` queue-it-for-later
      // pattern — Stop must always work eventually, whereas Treating must
      // never take effect without server confirmation.
      const id = alertIdRef.current;
      if (synthetic) return null;
      if (!id) return "not_ready";
      // ⚠ CYCLE 404: THE OPTIMISTIC WINDOW IS THE SERVER'S SINGLE
      // DURATION. Cycle 427: every window, however many — no cap.
      setOptimisticTreatingUntil(Date.now() + TREATING_WINDOW_SECONDS * 1000);
      try {
        const r = await alertsApi.treat(id);
        if (r.ok) lastTreatWindowRef.current = r.data?.custody_opened_at ?? null;
        if (!r.ok) {
          // ⚠ CYCLE 429 — A STALE-ROW REFUSAL EARNS ONE RETRY. On
          // 2026-09-10 twenty-two taps were refused `not_urgent_low`
          // because this slot was still bound to a row a dead socket had
          // delivered hours earlier. The refusal was right about the ROW
          // and wrong about the MEMBER: a live urgent-low row existed and
          // the kiosk was on it. So: re-resolve from server truth (the
          // member's open rows, REST — the socket is the thing in doubt),
          // and re-send the SAME tap ONCE to the live urgent-low row,
          // naming the refused row as `retry_of` so the door line reads
          // "refused stale, retried live, opened". No live row → the
          // refusal stands and renders exactly as before. Never a second
          // retry: the 6/60 s flood guard must stay unreachable by a
          // client, and a human must never reach it because of one.
          const code = treatingErrorCode(r.error);
          if (refusalMeansStaleRow(code)) {
            const listed = await alertsApi.list(24, 50);
            const live = listed.ok
              ? pickLiveUrgentRow(listed.data.alerts, id)
              : null;
            if (live != null) {
              const r2 = await alertsApi.treat(live.id, undefined, { retryOf: id });
              // Cycle 431: the retry is a client-claimed row on the next
              // activation report (the server has the two door lines; this
              // says the phone KNEW its slot was stale).
              recordClientEvent("stale_slot_retry", {
                refused_code: code ?? "unknown",
                retry_ok: r2.ok,
              });
              if (r2.ok) {
                lastTreatWindowRef.current = r2.data?.custody_opened_at ?? null;
                // The slot follows server truth: bind to the row that
                // opened — id AND level, so the episode does not carry a
                // stale `startLevel` from the row it was refused on — and
                // let the optimistic window stand until the row's own
                // frame confirms it.
                setAlertId(live.id);
                setStartLevel(live.level as AlertLevel);
                setOptimisticSnoozeUntil(null);
                setOptimisticSilenced(false);
                setAckFailure(null);
                return null;
              }
              setOptimisticTreatingUntil(null);
              return treatingErrorCode(r2.error);
            }
          }
          // The server refused. DROP the optimistic window immediately —
          // a refusal means the alarm must keep sounding (the dark-sensor
          // case is exactly this), and leaving the optimistic value in
          // place would quiet the one surface whose refusal we are
          // reporting.
          setOptimisticTreatingUntil(null);
          // Cycle 429 (the cycle-412 rule, applied to the treating door):
          // a tap addressed to a RECOVERED row with no live row to retry on
          // is a stale slot — re-seed from server truth now, not at the
          // 90 s tick.
          if (treatingErrorCode(r.error) === "episode_closed") {
            setReconcileNonce((n) => n + 1);
          }
          return code;
        }
        return null;
      } catch {
        setOptimisticTreatingUntil(null);
        return "failed";
      }
    },
    [isActive, synthetic],
  );

  // ─── Cycle 410: "Sound the alarm now" ──────────────────────────────
  // ONE DIRECTION. This device sounds on the tap (the override above), and
  // the POST ends the window for everyone else via the teardown frame. A
  // refusal or a network fault changes nothing about this device's audio:
  // it is already sounding, which is the direction that needs no gate.
  const soundNow = useCallback(async (): Promise<string | null> => {
    const id = alertIdRef.current;
    if (id) setLeadOverriddenAlertId(id);
    if (!id || synthetic) return null;
    try {
      const r = await alertsApi.soundNow(id);
      return r.ok ? null : "failed";
    } catch {
      return "failed";
    }
  }, [synthetic]);

  const setTreatingIdentityFn = useCallback(
    async (contactId: string | null): Promise<void> => {
      const id = alertIdRef.current;
      if (!id || synthetic) return;
      try {
        // Fire and forget by design. The window is ALREADY open and its
        // safety does not depend on this call, so a failure must not
        // surface anything over a live alarm — log it and move on.
        const r = await alertsApi.setTreatingIdentity(id, contactId);
        if (!r.ok) {
          console.warn("treating identity not recorded", r.error?.status);
        }
      } catch (e) {
        // ⚠ CYCLE 416 — DELIBERATELY NOT AN ACK, and the only catch on this
        // surface that stays quiet for the user. This records WHO is
        // treating; the custody window is ALREADY OPEN and its safety does
        // not depend on this call. Nothing optimistic is set, no audio is
        // muted, no countdown starts — so a failure cannot leave the UI
        // looking handled, which is the property `ackFailure` protects. It
        // still must not raise a dialog over a live urgent low, so it is
        // LOGGED rather than swallowed.
        // ⚠ `e.name` only — a fetch rejection's message can carry the request
        // URL, which embeds the alert id. Browser console, but free to fix.
        console.warn(
          "treating identity not recorded",
          e instanceof Error ? e.name : "error",
        );
      }
    },
    [synthetic],
  );

  const silenceFn = useCallback(() => {
    // Optimistic flip; same reasoning as address().
    setOptimisticSilenced(true);
    if (!alertId || synthetic) {
      setLocalSilenced(true);
      return;
    }
    void (async () => {
      {
        // Cycle 416: retried on a fault with no answer, then SURFACED. The
        // cycle-409/410 audio rules below are unchanged; only the silence
        // about the failure is gone.
        const r = await runAck("stop", () => alertsApi.silence(alertId));
        if (!r.ok && r.error.kind !== "network") {
          // Cycle 410: the SERVER refused — `409 not_silenceable_urgent_low`
          // on a stale client, a 404 — so there is no silence. Withdraw the
          // optimistic mute and sound again; a network fault keeps it (the
          // cycle-409 snooze-door rule, in the loud direction).
          setOptimisticSilenced(false);
          setLocalSilenced(false);
          // Cycle 412: a Stop addressed to a RECOVERED row is a stale slot —
          // re-seed from server truth now, not at the 90 s tick.
          if (treatingErrorCode(r.error) === "episode_closed") {
            setReconcileNonce((n) => n + 1);
          }
        }
      }
    })();
    // ⚠ Cycle 416: the swallowed `catch {}` that used to end this block is
    // GONE — that flip is precisely what made a failed Stop look like a successful
    // one.
  }, [alertId, synthetic, runAck]);

  const stop = useCallback(() => {
    // Cycle 62 + cycle 66: cross-device Stop.
    //
    // - Optimistic local mute fires IMMEDIATELY (independent of
    //   network, of `alarmActive`, and of `alertId` being captured).
    //   `setOptimisticSilenced(true)` makes `silenced` true on the
    //   next render → `audioOn` flips false → the audio bridge stops
    //   the loop on this device on the next tick.
    // - POSTs `/api/alerts/{id}/silence` so every other device on
    //   the cycle-60 member_audience converges to the same shared
    //   state.
    // - When no `alertId` is captured yet, queue the POST via
    //   `pendingSilenceRef` — the alertId-watch effect above fires
    //   it as soon as the next heartbeat / snapshot lands an id.
    //   Never a local-only no-op.
    if (!isActive) return;
    // Cycle 73 safety floor: urgent_low is NOT silenceable. The UI
    // gating (UrgentOverlay / NowScreen inline / UrgentBanner /
    // AlarmSilenceGuard) removes the Stop button on this level, but
    // this guard makes the hook the last line of defense — a stray
    // caller (a future component, a deep-link, programmatic
    // dismissal, a dev console) cannot silence a hypoglycemic event
    // even if it tries. `synthetic` previews still terminate via
    // `cancelPreview()` upstream (PreviewOverlay's syntheticEpisode
    // wires stop/stopNow directly to cancelPreview), so this guard
    // only fires on the live wire path.
    if (!synthetic && startLevel === "urgent_low") return;
    // Cycle 408: and the EFFECTIVE level — which folds in the server-bound
    // row — so a cold-start misclassification can never let Stop through
    // on a real urgent low (the safety review's finding 3).
    if (!synthetic && episodeLevelRef.current === "urgent_low") return;
    setOptimisticSilenced(true);
    if (synthetic) return;
    if (alertId) {
      void (async () => {
        // Cycle 416: retried on a fault with no answer, then SURFACED. The
        // swallowed `catch {}` that used to end this block is gone.
        const r = await runAck("stop", () => alertsApi.silence(alertId));
        if (!r.ok && r.error.kind !== "network") {
          // Cycle 410: the SERVER refused — `409 not_silenceable_urgent_low`
          // on a stale client, a 404 — so there is no silence. Withdraw the
          // optimistic mute and sound again; a network fault keeps it (the
          // cycle-409 snooze-door rule, in the loud direction).
          setOptimisticSilenced(false);
          setLocalSilenced(false);
          if (treatingErrorCode(r.error) === "episode_closed") {
            setReconcileNonce((n) => n + 1);
          }
        }
      })();
    } else {
      pendingSilenceRef.current = true;
      // Belt-and-suspenders: localSilenced keeps the surface in the
      // stopped treatment even if the optimistic clear-on-server-
      // sync effect fires before the queued POST does.
      setLocalSilenced(true);
    }
  }, [isActive, alertId, synthetic, startLevel, runAck]);

  const stopNow = useCallback(() => {
    // Cycle 63 + cycle 66 bulletproof Stop. Same cross-device path
    // as `stop()` but never gated on `isActive`. Opens the local
    // episode when the live reading is alarming so the post-Stop
    // treatment renders on this device too. `silenced` (via
    // optimisticSilenced) flips immediately so audio mutes; the
    // POST is fired or queued the same way as `stop()`.
    //
    // Cycle 73: same urgent_low safety floor as `stop()` above —
    // check both `startLevel` (the latched episode level) and the
    // live `currentLevel`, because `stopNow()` can be called BEFORE
    // an episode has been opened (the cycle-63 no-episode-yet case)
    // and we need to refuse the silence before the open below
    // commits an urgent_low episode into a silenced state. See
    // the stop() comment for the full rationale.
    if (
      !synthetic &&
      (startLevel === "urgent_low" ||
        currentLevel === "urgent_low" ||
        episodeLevelRef.current === "urgent_low")
    ) {
      return;
    }
    if (
      !isActive &&
      currentLevel != null &&
      ALARMING_LEVELS.has(currentLevel)
    ) {
      setIsActive(true);
      setStartLevel(currentLevel);
      setLocalSnoozeUntil(null);
      setLocalSilenced(false);
      setOptimisticSnoozeUntil(null);
    }
    setOptimisticSilenced(true);
    if (synthetic) return;
    if (alertId) {
      void (async () => {
        // Cycle 416: retried on a fault with no answer, then SURFACED. The
        // swallowed `catch {}` that used to end this block is gone.
        const r = await runAck("stop", () => alertsApi.silence(alertId));
        if (!r.ok && r.error.kind !== "network") {
          // Cycle 410: the SERVER refused — `409 not_silenceable_urgent_low`
          // on a stale client, a 404 — so there is no silence. Withdraw the
          // optimistic mute and sound again; a network fault keeps it (the
          // cycle-409 snooze-door rule, in the loud direction).
          setOptimisticSilenced(false);
          setLocalSilenced(false);
          if (treatingErrorCode(r.error) === "episode_closed") {
            setReconcileNonce((n) => n + 1);
          }
        }
      })();
    } else {
      pendingSilenceRef.current = true;
      setLocalSilenced(true);
    }
  }, [isActive, currentLevel, alertId, synthetic, startLevel, runAck]);

  // ─── Cycle 416: the retry affordance behind the visible failure ──────
  // ⚠ The banner never offers a bare dismiss. A caregiver who cannot make
  // the ack land must be able to try again from the surface that told them
  // it failed — clearing the notice without re-attempting would put the UI
  // straight back into the state this cycle abolishes.
  //
  // ⚠ It re-runs the ack the user ACTUALLY pressed. It must never upgrade a
  // refused Snooze into a Stop: Urgent Low is Snooze-only.
  const retryAck = useCallback(() => {
    const f = ackFailureRef.current;
    if (f == null) return;
    if (f.action === "snooze") {
      // ⚠ `retry: true` — see the guard in `address`. Without it this button
      // is a no-op for the entire optimistic window, which is exactly when it
      // is on screen.
      void address({ retry: true });
      return;
    }
    // `stop()` needs no equivalent: its only gate is `!isActive` (plus the
    // urgent-low floor, which must never be bypassed).
    stop();
  }, [address, stop]);

  // ─── Cycle 407: the check-in ────────────────────────────────────────
  //
  // The QUESTION is server state on the row (so a restarted kiosk resumes
  // it from the snapshot); the EVENT is the live frame (so the screen
  // speaks once per posed question). `answeredCheckinKey` is the only
  // local optimism and it can only HIDE a question this device just
  // answered, never show one — a failed POST un-hides it.
  // Cycle 427: the prompt mechanism — the server's live prompt KIND and
  // DEADLINE ride the row (`custody_prompt_kind` / `custody_prompt_deadline`)
  // and the `custody_checkin` frame is still the speech edge. Pending on
  // a window of EITHER kind: the dark-expiry check-in and the
  // still-treating question are posed on a snooze as well.
  const checkinQuestion = serverSourceAlert?.custody_prompt_kind ?? null;
  const checkinExpiresAt = serverSourceAlert?.custody_prompt_deadline ?? null;
  const currentCheckinKey = checkinKey(
    serverSourceAlert?.id,
    checkinQuestion,
    checkinExpiresAt,
  );
  const checkinPending =
    (treating || quietWindow) &&
    currentCheckinKey != null &&
    checkinPendingAt(Date.now(), checkinQuestion, checkinExpiresAt) &&
    answeredCheckinKey !== currentCheckinKey;
  const checkinEvent =
    socket.lastCheckinEvent != null &&
    alertId != null &&
    socket.lastCheckinEvent.alertId === alertId
      ? socket.lastCheckinEvent
      : null;
  const checkinFn = useCallback(
    async (
      answer: CustodyCheckinAnswer,
      kind?: CustodyCheckinQuestion | null,
    ): Promise<CustodyCheckinOutcome | "timeout" | null> => {
      if (!alertId || synthetic) return null;
      const key = currentCheckinKey;
      const quiet = answer === "yes" || answer === "no";
      if (key && quiet) {
        setAnsweredCheckinKey(key);
      }
      // Cycle 427: a quiet answer names the prompt it answers — the one
      // on screen — so the server refuses it if that prompt was replaced.
      const answeredKind = quiet ? (kind ?? checkinQuestion) : null;
      // ⚠ A HUNG POST MUST NEVER LEAVE THE SCREEN STUCK. `apiClient` has
      // no abort signal, so the request races a ceiling; on timeout the
      // question is UN-HIDDEN (the server may or may not have seen it — a
      // retry gets `resolved` if it did) and the caller re-enables its
      // control. The late result, if any, is ignored: the row's own frame
      // carries the truth.
      const r = await Promise.race<
        Awaited<ReturnType<typeof alertsApi.checkin>> | "timeout"
      >([
        alertsApi.checkin(alertId, answer, answeredKind),
        new Promise<"timeout">((resolve) =>
          window.setTimeout(() => resolve("timeout"), CHECKIN_REQUEST_TIMEOUT_MS),
        ),
      ]);
      if (r === "timeout") {
        setAnsweredCheckinKey((k) => (k === key ? null : k));
        return "timeout";
      }
      if (!r.ok) {
        // Un-hide: the server never saw the answer, so the question
        // stands. Loud direction.
        setAnsweredCheckinKey((k) => (k === key ? null : k));
        return null;
      }
      return r.data.outcome;
    },
    [alertId, synthetic, currentCheckinKey, checkinQuestion],
  );

  return {
    startLevel,
    phase,
    level: currentLevel,
    sgv: displaySgv,
    alertId,
    audioOn,
    hasSnoozed,
    silenced,
    stopped,
    snoozeRemainingMs,
    treating,
    treatingUntil: treatingUntilMs,
    treatingRemainingMs,
    treatingCount,
    custodyKind,
    quietWindow,
    audioLead,
    audioLeadUntil,
    audioLeadRemainingMs,
    audioLeadPending,
    audioLeadPendingUntil,
    audibleLevel,
    ascentLatched,
    audible,
    soundNow,
    treat,
    lastTreatWindow,
    treatingContactName: serverSourceAlert?.custody_contact_name ?? null,
    treatingBy: serverSourceAlert?.treating_by ?? null,
    custodyOpenedAt: serverSourceAlert?.custody_opened_at ?? null,
    attributionPickerSeconds:
      serverSourceAlert?.attribution_picker_seconds ?? 45,
    backupReach: serverSourceAlert?.custody_backup_reach ?? null,
    episodeOptOuts: serverSourceAlert?.episode_opt_outs ?? null,
    checkinQuestion,
    checkinExpiresAt,
    checkinPending,
    checkinEvent,
    treatingExtensionCount: serverSourceAlert?.custody_extension_count ?? 0,
    promptKind: checkinQuestion,
    promptDeadline: checkinExpiresAt,
    helpState: serverSourceAlert?.custody_help_state ?? null,
    helpContactName: serverSourceAlert?.custody_help_contact_name ?? null,
    noDataQuiet,
    checkin: checkinFn,
    custodyHandBack: socket.custodyHandBack,
    setTreatingIdentity: setTreatingIdentityFn,
    alarmActive,
    isLive,
    episodeLevel,
    bannerSuppressedInRange,
    inRangeStandDownAt,
    recoveringFromUrgentLow,
    address,
    // Cycle 416: non-null whenever an ack did not land. Surfaces MUST
    // render it — see `AlarmEpisodeState.ackFailure`.
    ackFailure,
    retryAck,
    silence: silenceFn,
    stop,
    stopNow,
    endEpisode: reset,
  };
}
